export type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

export type Database = {
  // Allows to automatically instantiate createClient with right options
  // instead of createClient<Database, { PostgrestVersion: 'XX' }>(URL, KEY)
  __InternalSupabase: {
    PostgrestVersion: "12.2.3";
  };
  graphql_public: {
    Tables: {
      [_ in never]: never;
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      graphql: {
        Args: {
          extensions?: Json;
          operationName?: string;
          query?: string;
          variables?: Json;
        };
        Returns: Json;
      };
    };
    Enums: {
      [_ in never]: never;
    };
    CompositeTypes: {
      [_ in never]: never;
    };
  };
  public: {
    Tables: {
      ai_audit_logs: {
        Row: {
          confidence_score: number | null;
          created_at: string;
          final_codes: Json | null;
          id: string;
          input_text: string;
          metadata: Json | null;
          operation_type: Database["public"]["Enums"]["ai_operation_type"];
          processing_duration: number | null;
          selected_fields: Json | null;
          template_id: string | null;
          user_corrections: Json | null;
        };
        Insert: {
          confidence_score?: number | null;
          created_at?: string;
          final_codes?: Json | null;
          id?: string;
          input_text: string;
          metadata?: Json | null;
          operation_type: Database["public"]["Enums"]["ai_operation_type"];
          processing_duration?: number | null;
          selected_fields?: Json | null;
          template_id?: string | null;
          user_corrections?: Json | null;
        };
        Update: {
          confidence_score?: number | null;
          created_at?: string;
          final_codes?: Json | null;
          id?: string;
          input_text?: string;
          metadata?: Json | null;
          operation_type?: Database["public"]["Enums"]["ai_operation_type"];
          processing_duration?: number | null;
          selected_fields?: Json | null;
          template_id?: string | null;
          user_corrections?: Json | null;
        };
        Relationships: [
          {
            foreignKeyName: "ai_audit_logs_template_id_fkey";
            columns: ["template_id"];
            isOneToOne: false;
            referencedRelation: "templates";
            referencedColumns: ["id"];
          },
        ];
      };
      audit_events: {
        Row: {
          created_at: string;
          encounter_id: string | null;
          id: string;
          payload: Json;
          type: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          encounter_id?: string | null;
          id?: string;
          payload: Json;
          type: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          encounter_id?: string | null;
          id?: string;
          payload?: Json;
          type?: string;
          user_id?: string | null;
        };
        Relationships: [];
      };
      audit_scheduled_runs: {
        Row: {
          claimed_at: string;
          occurrence: string;
          run_id: string | null;
        };
        Insert: {
          claimed_at: string;
          occurrence: string;
          run_id?: string | null;
        };
        Update: {
          claimed_at?: string;
          occurrence?: string;
          run_id?: string | null;
        };
        Relationships: [];
      };
      auth_migrations: {
        Row: {
          created_at: number | null;
          hash: string;
          id: number;
        };
        Insert: {
          created_at?: number | null;
          hash: string;
          id?: number;
        };
        Update: {
          created_at?: number | null;
          hash?: string;
          id?: number;
        };
        Relationships: [];
      };
      automated_writeback_rules: {
        Row: {
          conditions: Json;
          created_at: string;
          id: string;
          is_active: boolean;
          label: string;
          provider_id: Database["public"]["Enums"]["customer_provider_id"] | null;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"];
          updated_at: string;
        };
        Insert: {
          conditions: Json;
          created_at?: string;
          id?: string;
          is_active?: boolean;
          label: string;
          provider_id?: Database["public"]["Enums"]["customer_provider_id"] | null;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"];
          updated_at?: string;
        };
        Update: {
          conditions?: Json;
          created_at?: string;
          id?: string;
          is_active?: boolean;
          label?: string;
          provider_id?: Database["public"]["Enums"]["customer_provider_id"] | null;
          specialty?: Database["public"]["Enums"]["encounter_speciality_type"];
          updated_at?: string;
        };
        Relationships: [];
      };
      chemo_classification_specific_results: {
        Row: {
          automation_decline_reason: Database["public"]["Enums"]["chemo_automation_decline_reason"] | null;
          classification_result_id: string;
          coding_route: Database["public"]["Enums"]["chemo_coding_route"];
          created_at: string;
          id: string;
          is_first_day_of_cycle: boolean;
          source_encounter_id: string | null;
          updated_at: string;
        };
        Insert: {
          automation_decline_reason?: Database["public"]["Enums"]["chemo_automation_decline_reason"] | null;
          classification_result_id: string;
          coding_route?: Database["public"]["Enums"]["chemo_coding_route"];
          created_at?: string;
          id?: string;
          is_first_day_of_cycle: boolean;
          source_encounter_id?: string | null;
          updated_at?: string;
        };
        Update: {
          automation_decline_reason?: Database["public"]["Enums"]["chemo_automation_decline_reason"] | null;
          classification_result_id?: string;
          coding_route?: Database["public"]["Enums"]["chemo_coding_route"];
          created_at?: string;
          id?: string;
          is_first_day_of_cycle?: boolean;
          source_encounter_id?: string | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "chemo_classification_specific_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "chemo_classification_specific_results_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      classification_automation_decisions: {
        Row: {
          automation_decline_reason: Database["public"]["Enums"]["automation_decline_reason"] | null;
          classification_result_id: string;
          coding_route: Database["public"]["Enums"]["coding_route"];
          created_at: string;
          id: string;
          source_encounter_id: string | null;
          strategy: Database["public"]["Enums"]["automation_strategy"] | null;
          updated_at: string;
        };
        Insert: {
          automation_decline_reason?: Database["public"]["Enums"]["automation_decline_reason"] | null;
          classification_result_id: string;
          coding_route?: Database["public"]["Enums"]["coding_route"];
          created_at?: string;
          id?: string;
          source_encounter_id?: string | null;
          strategy?: Database["public"]["Enums"]["automation_strategy"] | null;
          updated_at?: string;
        };
        Update: {
          automation_decline_reason?: Database["public"]["Enums"]["automation_decline_reason"] | null;
          classification_result_id?: string;
          coding_route?: Database["public"]["Enums"]["coding_route"];
          created_at?: string;
          id?: string;
          source_encounter_id?: string | null;
          strategy?: Database["public"]["Enums"]["automation_strategy"] | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "classification_automation_decisions_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "classification_automation_decisions_source_encounter_id_fkey";
            columns: ["source_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      classification_result_diagnosis_evidence: {
        Row: {
          created_at: string;
          description: string;
          icd10_code: string;
          id: string;
          inbound_classification_result_id: string;
          timeline: Json;
        };
        Insert: {
          created_at?: string;
          description: string;
          icd10_code: string;
          id?: string;
          inbound_classification_result_id: string;
          timeline: Json;
        };
        Update: {
          created_at?: string;
          description?: string;
          icd10_code?: string;
          id?: string;
          inbound_classification_result_id?: string;
          timeline?: Json;
        };
        Relationships: [
          {
            foreignKeyName: "classification_result_diagnosis_evidence_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_result_diagnosis_evidence_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_result_diagnosis_evidence_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_result_diagnosis_evidence_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "classification_result_diagnosis_evidence_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_result_diagnosis_evidence_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
        ];
      };
      classification_result_unmatched_lead_terms: {
        Row: {
          anatomical_locations: string[];
          created_at: string;
          debug_info: string | null;
          evidence_quotes: Json;
          id: string;
          inbound_classification_result_id: string;
          langfuse_span_id: string | null;
          langfuse_trace_id: string | null;
          lead_term: string;
          term_chain: Json;
        };
        Insert: {
          anatomical_locations?: string[];
          created_at?: string;
          debug_info?: string | null;
          evidence_quotes: Json;
          id?: string;
          inbound_classification_result_id: string;
          langfuse_span_id?: string | null;
          langfuse_trace_id?: string | null;
          lead_term: string;
          term_chain: Json;
        };
        Update: {
          anatomical_locations?: string[];
          created_at?: string;
          debug_info?: string | null;
          evidence_quotes?: Json;
          id?: string;
          inbound_classification_result_id?: string;
          langfuse_span_id?: string | null;
          langfuse_trace_id?: string | null;
          lead_term?: string;
          term_chain?: Json;
        };
        Relationships: [
          {
            foreignKeyName: "classification_result_unmatched_lead_terms_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_result_unmatched_lead_terms_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_result_unmatched_lead_terms_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_result_unmatched_lead_terms_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "classification_result_unmatched_lead_terms_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "classification_result_unmatched_lead_terms_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
        ];
      };
      clinical_code_transformation_condition_sets: {
        Row: {
          created_at: string;
          id: string;
          name: string;
          transformation_id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          name?: string;
          transformation_id: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          name?: string;
          transformation_id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "clinical_code_transformation_condition_s_transformation_id_fkey";
            columns: ["transformation_id"];
            isOneToOne: false;
            referencedRelation: "clinical_code_transformation_outputs_view";
            referencedColumns: ["transformation_id"];
          },
          {
            foreignKeyName: "clinical_code_transformation_condition_s_transformation_id_fkey";
            columns: ["transformation_id"];
            isOneToOne: false;
            referencedRelation: "clinical_code_transformations";
            referencedColumns: ["id"];
          },
        ];
      };
      clinical_code_transformation_conditions: {
        Row: {
          condition_set_id: string;
          created_at: string;
          datapoint_id: string;
          field_id: string;
          id: string;
          operator: Database["public"]["Enums"]["transformation_operator"];
          updated_at: string;
        };
        Insert: {
          condition_set_id: string;
          created_at?: string;
          datapoint_id: string;
          field_id: string;
          id?: string;
          operator?: Database["public"]["Enums"]["transformation_operator"];
          updated_at?: string;
        };
        Update: {
          condition_set_id?: string;
          created_at?: string;
          datapoint_id?: string;
          field_id?: string;
          id?: string;
          operator?: Database["public"]["Enums"]["transformation_operator"];
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "clinical_code_transformation_conditions_condition_set_id_fkey";
            columns: ["condition_set_id"];
            isOneToOne: false;
            referencedRelation: "clinical_code_transformation_condition_sets";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "clinical_code_transformation_conditions_datapoint_id_fkey";
            columns: ["datapoint_id"];
            isOneToOne: false;
            referencedRelation: "datapoints";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "clinical_code_transformation_conditions_field_id_fkey";
            columns: ["field_id"];
            isOneToOne: false;
            referencedRelation: "fields";
            referencedColumns: ["id"];
          },
        ];
      };
      clinical_code_transformations: {
        Row: {
          code_id: string;
          code_type: Database["public"]["Enums"]["clinical_code_type"];
          created_at: string;
          display_order: string;
          id: string;
          updated_at: string;
        };
        Insert: {
          code_id: string;
          code_type: Database["public"]["Enums"]["clinical_code_type"];
          created_at?: string;
          display_order?: string;
          id?: string;
          updated_at?: string;
        };
        Update: {
          code_id?: string;
          code_type?: Database["public"]["Enums"]["clinical_code_type"];
          created_at?: string;
          display_order?: string;
          id?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      clinical_code_uploads: {
        Row: {
          duplicates_found: number | null;
          duplicates_skipped: number | null;
          duplicates_updated: number | null;
          error_message: string | null;
          filename: string;
          id: string;
          status: string;
          total_codes: number;
          uploaded_at: string | null;
        };
        Insert: {
          duplicates_found?: number | null;
          duplicates_skipped?: number | null;
          duplicates_updated?: number | null;
          error_message?: string | null;
          filename: string;
          id?: string;
          status?: string;
          total_codes: number;
          uploaded_at?: string | null;
        };
        Update: {
          duplicates_found?: number | null;
          duplicates_skipped?: number | null;
          duplicates_updated?: number | null;
          error_message?: string | null;
          filename?: string;
          id?: string;
          status?: string;
          total_codes?: number;
          uploaded_at?: string | null;
        };
        Relationships: [];
      };
      codeable_summary_bundles: {
        Row: {
          bundle_type: Database["public"]["Enums"]["file_identifier"] | null;
          contributed_at: string;
          encounter_id: string;
          raw_bundle_id: string;
        };
        Insert: {
          bundle_type?: Database["public"]["Enums"]["file_identifier"] | null;
          contributed_at?: string;
          encounter_id: string;
          raw_bundle_id: string;
        };
        Update: {
          bundle_type?: Database["public"]["Enums"]["file_identifier"] | null;
          contributed_at?: string;
          encounter_id?: string;
          raw_bundle_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "codeable_summary_bundles_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "codeable_summary_states";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_bundles_raw_bundle_id_fkey";
            columns: ["raw_bundle_id"];
            isOneToOne: false;
            referencedRelation: "raw_files_bundles";
            referencedColumns: ["id"];
          },
        ];
      };
      codeable_summary_skipped_pages: {
        Row: {
          confidence: string;
          created_at: string;
          encounter_id: string;
          file_key: string;
          page_number: number;
          prompt_version: number;
          raw_bundle_id: string;
        };
        Insert: {
          confidence: string;
          created_at?: string;
          encounter_id: string;
          file_key: string;
          page_number: number;
          prompt_version: number;
          raw_bundle_id: string;
        };
        Update: {
          confidence?: string;
          created_at?: string;
          encounter_id?: string;
          file_key?: string;
          page_number?: number;
          prompt_version?: number;
          raw_bundle_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "codeable_summary_skipped_pages_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "codeable_summary_states";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_skipped_pages_raw_bundle_id_fkey";
            columns: ["raw_bundle_id"];
            isOneToOne: false;
            referencedRelation: "raw_files_bundles";
            referencedColumns: ["id"];
          },
        ];
      };
      codeable_summary_states: {
        Row: {
          created_at: string;
          derived_relevance_version: number | null;
          emitted_at: string | null;
          encounter_id: string;
          notified_at: string | null;
          patient_document_id: string | null;
          status: Database["public"]["Enums"]["codeable_summary_state_status"];
          stuck_at_relevance_version: number | null;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          derived_relevance_version?: number | null;
          emitted_at?: string | null;
          encounter_id: string;
          notified_at?: string | null;
          patient_document_id?: string | null;
          status?: Database["public"]["Enums"]["codeable_summary_state_status"];
          stuck_at_relevance_version?: number | null;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          derived_relevance_version?: number | null;
          emitted_at?: string | null;
          encounter_id?: string;
          notified_at?: string | null;
          patient_document_id?: string | null;
          status?: Database["public"]["Enums"]["codeable_summary_state_status"];
          stuck_at_relevance_version?: number | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "codeable_summary_states_patient_document_id_fkey";
            columns: ["patient_document_id"];
            isOneToOne: false;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
        ];
      };
      coding_investigation_findings: {
        Row: {
          code: string;
          coding_system: Database["public"]["Enums"]["clinical_code_type"];
          confidence: Database["public"]["Enums"]["coding_investigation_confidence"];
          created_at: string;
          discrepancy_kind: Database["public"]["Enums"]["coding_investigation_discrepancy_kind"];
          id: string;
          investigation_id: string;
          known_pattern_ref: string | null;
          root_cause_category: string;
          suggested_fix: string;
          suspected_prompt_name: string | null;
          suspected_prompt_version: number | null;
          suspected_step: string;
        };
        Insert: {
          code: string;
          coding_system: Database["public"]["Enums"]["clinical_code_type"];
          confidence: Database["public"]["Enums"]["coding_investigation_confidence"];
          created_at?: string;
          discrepancy_kind: Database["public"]["Enums"]["coding_investigation_discrepancy_kind"];
          id?: string;
          investigation_id: string;
          known_pattern_ref?: string | null;
          root_cause_category: string;
          suggested_fix: string;
          suspected_prompt_name?: string | null;
          suspected_prompt_version?: number | null;
          suspected_step: string;
        };
        Update: {
          code?: string;
          coding_system?: Database["public"]["Enums"]["clinical_code_type"];
          confidence?: Database["public"]["Enums"]["coding_investigation_confidence"];
          created_at?: string;
          discrepancy_kind?: Database["public"]["Enums"]["coding_investigation_discrepancy_kind"];
          id?: string;
          investigation_id?: string;
          known_pattern_ref?: string | null;
          root_cause_category?: string;
          suggested_fix?: string;
          suspected_prompt_name?: string | null;
          suspected_prompt_version?: number | null;
          suspected_step?: string;
        };
        Relationships: [
          {
            foreignKeyName: "coding_investigation_findings_investigation_id_fkey";
            columns: ["investigation_id"];
            isOneToOne: false;
            referencedRelation: "coding_investigations";
            referencedColumns: ["id"];
          },
        ];
      };
      coding_investigations: {
        Row: {
          created_at: string;
          encounter_id: string;
          error_message: string | null;
          id: string;
          inbound_classification_result_id: string;
          investigator_langfuse_trace_id: string | null;
          investigator_run_id: string | null;
          original_langfuse_trace_id: string | null;
          run_by_user_id: string | null;
          summary_markdown: string | null;
          triggered_by: Database["public"]["Enums"]["coding_investigation_trigger"];
        };
        Insert: {
          created_at?: string;
          encounter_id: string;
          error_message?: string | null;
          id?: string;
          inbound_classification_result_id: string;
          investigator_langfuse_trace_id?: string | null;
          investigator_run_id?: string | null;
          original_langfuse_trace_id?: string | null;
          run_by_user_id?: string | null;
          summary_markdown?: string | null;
          triggered_by?: Database["public"]["Enums"]["coding_investigation_trigger"];
        };
        Update: {
          created_at?: string;
          encounter_id?: string;
          error_message?: string | null;
          id?: string;
          inbound_classification_result_id?: string;
          investigator_langfuse_trace_id?: string | null;
          investigator_run_id?: string | null;
          original_langfuse_trace_id?: string | null;
          run_by_user_id?: string | null;
          summary_markdown?: string | null;
          triggered_by?: Database["public"]["Enums"]["coding_investigation_trigger"];
        };
        Relationships: [
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "coding_investigations_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "coding_investigations_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "coding_investigations_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "coding_investigations_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "coding_investigations_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "coding_investigations_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "coding_investigations_result_id_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
        ];
      };
      customers: {
        Row: {
          created_at: string;
          histology_wait_days: number | null;
          id: string;
          label: string;
          provider_id: Database["public"]["Enums"]["customer_provider_id"];
          review_cutoff_date: string | null;
          rules: string | null;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          histology_wait_days?: number | null;
          id?: string;
          label: string;
          provider_id: Database["public"]["Enums"]["customer_provider_id"];
          review_cutoff_date?: string | null;
          rules?: string | null;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          histology_wait_days?: number | null;
          id?: string;
          label?: string;
          provider_id?: Database["public"]["Enums"]["customer_provider_id"];
          review_cutoff_date?: string | null;
          rules?: string | null;
          updated_at?: string;
        };
        Relationships: [];
      };
      datapoints: {
        Row: {
          created_at: string;
          dataset_id: string | null;
          id: string;
          name: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          dataset_id?: string | null;
          id?: string;
          name: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          dataset_id?: string | null;
          id?: string;
          name?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "datapoints_dataset_id_fkey";
            columns: ["dataset_id"];
            isOneToOne: false;
            referencedRelation: "datasets";
            referencedColumns: ["id"];
          },
        ];
      };
      dataset_sync_runs: {
        Row: {
          encounters_processed: number;
          errors: Json;
          finished_at: string | null;
          id: string;
          items_upserted: number;
          outcome: string;
          started_at: string;
          triggered_by: string;
        };
        Insert: {
          encounters_processed?: number;
          errors?: Json;
          finished_at?: string | null;
          id?: string;
          items_upserted?: number;
          outcome: string;
          started_at: string;
          triggered_by: string;
        };
        Update: {
          encounters_processed?: number;
          errors?: Json;
          finished_at?: string | null;
          id?: string;
          items_upserted?: number;
          outcome?: string;
          started_at?: string;
          triggered_by?: string;
        };
        Relationships: [];
      };
      datasets: {
        Row: {
          created_at: string;
          description: string | null;
          id: string;
          name: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          description?: string | null;
          id?: string;
          name: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          description?: string | null;
          id?: string;
          name?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      demo_scenarios: {
        Row: {
          clinical_codes: Json;
          created_at: string;
          demo_text: string;
          id: string;
          updated_at: string;
        };
        Insert: {
          clinical_codes?: Json;
          created_at?: string;
          demo_text: string;
          id?: string;
          updated_at?: string;
        };
        Update: {
          clinical_codes?: Json;
          created_at?: string;
          demo_text?: string;
          id?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      discharge_cancelled_document_data: {
        Row: {
          admit_date: string;
          created_at: string | null;
          document_id: string;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"] | null;
          updated_at: string;
        };
        Insert: {
          admit_date: string;
          created_at?: string | null;
          document_id: string;
          specialty?: Database["public"]["Enums"]["encounter_speciality_type"] | null;
          updated_at?: string;
        };
        Update: {
          admit_date?: string;
          created_at?: string | null;
          document_id?: string;
          specialty?: Database["public"]["Enums"]["encounter_speciality_type"] | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "discharge_cancelled_document_data_document_id_fkey";
            columns: ["document_id"];
            isOneToOne: true;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
        ];
      };
      discharge_document_data: {
        Row: {
          admit_date: string | null;
          created_at: string | null;
          csn: string | null;
          discharge_date: string;
          document_id: string;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"] | null;
          updated_at: string;
        };
        Insert: {
          admit_date?: string | null;
          created_at?: string | null;
          csn?: string | null;
          discharge_date: string;
          document_id: string;
          specialty?: Database["public"]["Enums"]["encounter_speciality_type"] | null;
          updated_at?: string;
        };
        Update: {
          admit_date?: string | null;
          created_at?: string | null;
          csn?: string | null;
          discharge_date?: string;
          document_id?: string;
          specialty?: Database["public"]["Enums"]["encounter_speciality_type"] | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "discharge_document_data_document_id_fkey";
            columns: ["document_id"];
            isOneToOne: true;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
        ];
      };
      drizzle_migrations: {
        Row: {
          created_at: number | null;
          hash: string;
          id: number;
        };
        Insert: {
          created_at?: number | null;
          hash: string;
          id?: number;
        };
        Update: {
          created_at?: number | null;
          hash?: string;
          id?: number;
        };
        Relationships: [];
      };
      encounter_documents: {
        Row: {
          created_at: string | null;
          document_id: string;
          encounter_id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string | null;
          document_id: string;
          encounter_id: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string | null;
          document_id?: string;
          encounter_id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_documents_document_id_fkey";
            columns: ["document_id"];
            isOneToOne: false;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_documents_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      encounter_hrgs: {
        Row: {
          built_from_icd10_codes: string[];
          built_from_opcs4_codes: string[];
          created_at: string;
          dominant_procedure: string | null;
          encounter_id: string;
          errors: Json;
          grouper_database: string;
          grouper_release: string;
          hrg: string | null;
          id: string;
          input_record: string;
          opcs_substitutions: Json;
          status: Database["public"]["Enums"]["encounter_grouping_status"];
          unbundled_hrgs: string[];
        };
        Insert: {
          built_from_icd10_codes: string[];
          built_from_opcs4_codes: string[];
          created_at?: string;
          dominant_procedure?: string | null;
          encounter_id: string;
          errors: Json;
          grouper_database: string;
          grouper_release: string;
          hrg?: string | null;
          id?: string;
          input_record: string;
          opcs_substitutions: Json;
          status: Database["public"]["Enums"]["encounter_grouping_status"];
          unbundled_hrgs: string[];
        };
        Update: {
          built_from_icd10_codes?: string[];
          built_from_opcs4_codes?: string[];
          created_at?: string;
          dominant_procedure?: string | null;
          encounter_id?: string;
          errors?: Json;
          grouper_database?: string;
          grouper_release?: string;
          hrg?: string | null;
          id?: string;
          input_record?: string;
          opcs_substitutions?: Json;
          status?: Database["public"]["Enums"]["encounter_grouping_status"];
          unbundled_hrgs?: string[];
        };
        Relationships: [
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_hrgs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      encounter_human_review: {
        Row: {
          created_at: string;
          encounter_id: string;
          hrg_id: string | null;
          id: string;
          is_voided: boolean;
          notes: string | null;
          outcome: Database["public"]["Enums"]["human_review_outcome"] | null;
          reviewer_id: string;
          status: Database["public"]["Enums"]["human_review_status"];
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          encounter_id: string;
          hrg_id?: string | null;
          id?: string;
          is_voided?: boolean;
          notes?: string | null;
          outcome?: Database["public"]["Enums"]["human_review_outcome"] | null;
          reviewer_id: string;
          status?: Database["public"]["Enums"]["human_review_status"];
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          encounter_id?: string;
          hrg_id?: string | null;
          id?: string;
          is_voided?: boolean;
          notes?: string | null;
          outcome?: Database["public"]["Enums"]["human_review_outcome"] | null;
          reviewer_id?: string;
          status?: Database["public"]["Enums"]["human_review_status"];
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_human_review_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_human_review_hrg_id_fkey";
            columns: ["hrg_id"];
            isOneToOne: false;
            referencedRelation: "hrgs";
            referencedColumns: ["id"];
          },
        ];
      };
      encounter_human_review_diagnosis_code: {
        Row: {
          code: string;
          created_at: string;
          description: string | null;
          human_review_id: string;
          id: string;
          order_index: number;
        };
        Insert: {
          code: string;
          created_at?: string;
          description?: string | null;
          human_review_id: string;
          id?: string;
          order_index?: number;
        };
        Update: {
          code?: string;
          created_at?: string;
          description?: string | null;
          human_review_id?: string;
          id?: string;
          order_index?: number;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_human_review_diagnosis_code_human_review_id_fkey";
            columns: ["human_review_id"];
            isOneToOne: false;
            referencedRelation: "encounter_human_review";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "encounter_human_review_diagnosis_code_human_review_id_fkey";
            columns: ["human_review_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["review_id"];
          },
        ];
      };
      encounter_human_review_procedure_code: {
        Row: {
          code: string;
          created_at: string;
          description: string | null;
          human_review_id: string;
          id: string;
          order_index: number;
        };
        Insert: {
          code: string;
          created_at?: string;
          description?: string | null;
          human_review_id: string;
          id?: string;
          order_index?: number;
        };
        Update: {
          code?: string;
          created_at?: string;
          description?: string | null;
          human_review_id?: string;
          id?: string;
          order_index?: number;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_human_review_procedure_code_human_review_id_fkey";
            columns: ["human_review_id"];
            isOneToOne: false;
            referencedRelation: "encounter_human_review";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "encounter_human_review_procedure_code_human_review_id_fkey";
            columns: ["human_review_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["review_id"];
          },
        ];
      };
      encounter_indication_verdicts: {
        Row: {
          absent: boolean;
          created_at: string;
          decider_version: number;
          encounter_id: string;
          id: string;
          mention_count: number | null;
          mentions: Json;
          personal_history: boolean;
          position: number;
          reason: string;
          rule_id: string;
          rules_lost: string[];
          verdict: Database["public"]["Enums"]["indication_verdict"];
        };
        Insert: {
          absent: boolean;
          created_at?: string;
          decider_version: number;
          encounter_id: string;
          id?: string;
          mention_count?: number | null;
          mentions?: Json;
          personal_history?: boolean;
          position: number;
          reason: string;
          rule_id: string;
          rules_lost: string[];
          verdict: Database["public"]["Enums"]["indication_verdict"];
        };
        Update: {
          absent?: boolean;
          created_at?: string;
          decider_version?: number;
          encounter_id?: string;
          id?: string;
          mention_count?: number | null;
          mentions?: Json;
          personal_history?: boolean;
          position?: number;
          reason?: string;
          rule_id?: string;
          rules_lost?: string[];
          verdict?: Database["public"]["Enums"]["indication_verdict"];
        };
        Relationships: [
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_indication_verdicts_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      encounter_relevance_assessments: {
        Row: {
          assessed_at: string | null;
          attempts: number;
          claim_token: string | null;
          claimed_at: string | null;
          created_at: string;
          encounter_id: string;
          raw_bundle_id: string;
          status: Database["public"]["Enums"]["encounter_relevance_assessment_status"];
          updated_at: string;
          verdict_reason: string | null;
        };
        Insert: {
          assessed_at?: string | null;
          attempts?: number;
          claim_token?: string | null;
          claimed_at?: string | null;
          created_at?: string;
          encounter_id: string;
          raw_bundle_id: string;
          status?: Database["public"]["Enums"]["encounter_relevance_assessment_status"];
          updated_at?: string;
          verdict_reason?: string | null;
        };
        Update: {
          assessed_at?: string | null;
          attempts?: number;
          claim_token?: string | null;
          claimed_at?: string | null;
          created_at?: string;
          encounter_id?: string;
          raw_bundle_id?: string;
          status?: Database["public"]["Enums"]["encounter_relevance_assessment_status"];
          updated_at?: string;
          verdict_reason?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_assessments_raw_bundle_id_fkey";
            columns: ["raw_bundle_id"];
            isOneToOne: false;
            referencedRelation: "raw_files_bundles";
            referencedColumns: ["id"];
          },
        ];
      };
      encounter_relevance_review_items: {
        Row: {
          created_at: string;
          encounter_id: string;
          id: string;
          langfuse_dataset_item_id: string;
          langfuse_observation_id: string;
          langfuse_queue_item_id: string;
          langfuse_trace_id: string;
          raw_bundle_id: string;
          reviewed_at: string | null;
          reviewed_by: string | null;
          reviewed_reason: string | null;
          reviewed_relevant: boolean | null;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          encounter_id: string;
          id?: string;
          langfuse_dataset_item_id: string;
          langfuse_observation_id: string;
          langfuse_queue_item_id: string;
          langfuse_trace_id: string;
          raw_bundle_id: string;
          reviewed_at?: string | null;
          reviewed_by?: string | null;
          reviewed_reason?: string | null;
          reviewed_relevant?: boolean | null;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          encounter_id?: string;
          id?: string;
          langfuse_dataset_item_id?: string;
          langfuse_observation_id?: string;
          langfuse_queue_item_id?: string;
          langfuse_trace_id?: string;
          raw_bundle_id?: string;
          reviewed_at?: string | null;
          reviewed_by?: string | null;
          reviewed_reason?: string | null;
          reviewed_relevant?: boolean | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_relevance_review_items_encounter_raw_bundle_fkey";
            columns: ["encounter_id", "raw_bundle_id"];
            isOneToOne: true;
            referencedRelation: "encounter_relevance_assessments";
            referencedColumns: ["encounter_id", "raw_bundle_id"];
          },
        ];
      };
      encounter_relevance_states: {
        Row: {
          created_at: string;
          encounter_id: string;
          published_version: number;
          relevance_version: number;
          relevant_raw_bundle_ids: string[];
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          encounter_id: string;
          published_version?: number;
          relevance_version?: number;
          relevant_raw_bundle_ids: string[];
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          encounter_id?: string;
          published_version?: number;
          relevance_version?: number;
          relevant_raw_bundle_ids?: string[];
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_relevance_states_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: true;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      encounter_source_fetches: {
        Row: {
          bundle_count: number | null;
          created_at: string;
          deadline_at: string;
          encounter_id: string;
          outcome_reason: string | null;
          requested_at: string | null;
          responded_at: string | null;
          source: Database["public"]["Enums"]["raw_files_bundle_source"];
          state: Database["public"]["Enums"]["encounter_source_fetch_state"];
          updated_at: string;
        };
        Insert: {
          bundle_count?: number | null;
          created_at?: string;
          deadline_at: string;
          encounter_id: string;
          outcome_reason?: string | null;
          requested_at?: string | null;
          responded_at?: string | null;
          source: Database["public"]["Enums"]["raw_files_bundle_source"];
          state?: Database["public"]["Enums"]["encounter_source_fetch_state"];
          updated_at?: string;
        };
        Update: {
          bundle_count?: number | null;
          created_at?: string;
          deadline_at?: string;
          encounter_id?: string;
          outcome_reason?: string | null;
          requested_at?: string | null;
          responded_at?: string | null;
          source?: Database["public"]["Enums"]["raw_files_bundle_source"];
          state?: Database["public"]["Enums"]["encounter_source_fetch_state"];
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "encounter_source_fetches_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      endoscopy_appointment_document_data: {
        Row: {
          appointment_id: string;
          created_at: string;
          document_id: string;
          observation_datetime: string;
          updated_at: string;
        };
        Insert: {
          appointment_id: string;
          created_at?: string;
          document_id: string;
          observation_datetime: string;
          updated_at?: string;
        };
        Update: {
          appointment_id?: string;
          created_at?: string;
          document_id?: string;
          observation_datetime?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "endoscopy_appointment_document_data_document_id_fkey";
            columns: ["document_id"];
            isOneToOne: true;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
        ];
      };
      endoscopy_histology_document_data: {
        Row: {
          created_at: string | null;
          csn: string | null;
          document_id: string;
          observation_datetime: string;
          site: Database["public"]["Enums"]["histology_site"] | null;
          updated_at: string;
        };
        Insert: {
          created_at?: string | null;
          csn?: string | null;
          document_id: string;
          observation_datetime: string;
          site?: Database["public"]["Enums"]["histology_site"] | null;
          updated_at?: string;
        };
        Update: {
          created_at?: string | null;
          csn?: string | null;
          document_id?: string;
          observation_datetime?: string;
          site?: Database["public"]["Enums"]["histology_site"] | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "endoscopy_histology_document_data_document_id_fkey";
            columns: ["document_id"];
            isOneToOne: true;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
        ];
      };
      endoscopy_procedure_document_data: {
        Row: {
          appointment_id: string | null;
          created_at: string | null;
          csn: string | null;
          document_id: string;
          observation_datetime: string;
          observation_identifier: string | null;
          screening_programme: Database["public"]["Enums"]["screening_programme"] | null;
          updated_at: string;
        };
        Insert: {
          appointment_id?: string | null;
          created_at?: string | null;
          csn?: string | null;
          document_id: string;
          observation_datetime: string;
          observation_identifier?: string | null;
          screening_programme?: Database["public"]["Enums"]["screening_programme"] | null;
          updated_at?: string;
        };
        Update: {
          appointment_id?: string | null;
          created_at?: string | null;
          csn?: string | null;
          document_id?: string;
          observation_datetime?: string;
          observation_identifier?: string | null;
          screening_programme?: Database["public"]["Enums"]["screening_programme"] | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "endoscopy_procedure_document_data_document_id_fkey";
            columns: ["document_id"];
            isOneToOne: true;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
        ];
      };
      epr_settings: {
        Row: {
          created_at: string;
          epr_type: string;
          id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          epr_type: string;
          id?: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          epr_type?: string;
          id?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      expected_co_morbidities: {
        Row: {
          code_id: string;
          id: string;
          is_optional: boolean;
          medical_text_example_id: string | null;
          order_index: number;
        };
        Insert: {
          code_id: string;
          id?: string;
          is_optional?: boolean;
          medical_text_example_id?: string | null;
          order_index: number;
        };
        Update: {
          code_id?: string;
          id?: string;
          is_optional?: boolean;
          medical_text_example_id?: string | null;
          order_index?: number;
        };
        Relationships: [
          {
            foreignKeyName: "expected_co_morbidities_code_id_fkey";
            columns: ["code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "expected_co_morbidities_medical_text_example_id_fkey";
            columns: ["medical_text_example_id"];
            isOneToOne: false;
            referencedRelation: "medical_text_examples";
            referencedColumns: ["id"];
          },
        ];
      };
      expected_diagnosis: {
        Row: {
          code_id: string;
          id: string;
          is_optional: boolean;
          medical_text_example_id: string | null;
          order_index: number | null;
        };
        Insert: {
          code_id: string;
          id?: string;
          is_optional?: boolean;
          medical_text_example_id?: string | null;
          order_index?: number | null;
        };
        Update: {
          code_id?: string;
          id?: string;
          is_optional?: boolean;
          medical_text_example_id?: string | null;
          order_index?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "expected_diagnosis_code_id_fkey";
            columns: ["code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "expected_diagnosis_medical_text_example_id_fkey";
            columns: ["medical_text_example_id"];
            isOneToOne: false;
            referencedRelation: "medical_text_examples";
            referencedColumns: ["id"];
          },
        ];
      };
      expected_procedures: {
        Row: {
          code_id: string | null;
          id: string;
          is_optional: boolean;
          medical_text_example_id: string | null;
          order_index: number | null;
        };
        Insert: {
          code_id?: string | null;
          id?: string;
          is_optional?: boolean;
          medical_text_example_id?: string | null;
          order_index?: number | null;
        };
        Update: {
          code_id?: string | null;
          id?: string;
          is_optional?: boolean;
          medical_text_example_id?: string | null;
          order_index?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "expected_procedures_code_id_fkey";
            columns: ["code_id"];
            isOneToOne: false;
            referencedRelation: "opcs4_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "expected_procedures_medical_text_example_id_fkey";
            columns: ["medical_text_example_id"];
            isOneToOne: false;
            referencedRelation: "medical_text_examples";
            referencedColumns: ["id"];
          },
        ];
      };
      field_completions: {
        Row: {
          confidence: number | null;
          created_at: string;
          field_id: string;
          id: string;
          updated_at: string;
          values_with_confidence: Json | null;
        };
        Insert: {
          confidence?: number | null;
          created_at?: string;
          field_id: string;
          id?: string;
          updated_at?: string;
          values_with_confidence?: Json | null;
        };
        Update: {
          confidence?: number | null;
          created_at?: string;
          field_id?: string;
          id?: string;
          updated_at?: string;
          values_with_confidence?: Json | null;
        };
        Relationships: [
          {
            foreignKeyName: "field_completions_field_id_fkey";
            columns: ["field_id"];
            isOneToOne: false;
            referencedRelation: "fields";
            referencedColumns: ["id"];
          },
        ];
      };
      field_help_texts: {
        Row: {
          created_at: string;
          custom_text: string | null;
          default_text: string;
          field_id: string;
          id: string;
          updated_at: string;
          usage_count: number | null;
        };
        Insert: {
          created_at?: string;
          custom_text?: string | null;
          default_text: string;
          field_id: string;
          id?: string;
          updated_at?: string;
          usage_count?: number | null;
        };
        Update: {
          created_at?: string;
          custom_text?: string | null;
          default_text?: string;
          field_id?: string;
          id?: string;
          updated_at?: string;
          usage_count?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "field_help_texts_field_id_fkey";
            columns: ["field_id"];
            isOneToOne: false;
            referencedRelation: "fields";
            referencedColumns: ["id"];
          },
        ];
      };
      field_rules: {
        Row: {
          created_at: string;
          enabled: boolean;
          field_id: string;
          id: string;
          rule_text: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          enabled?: boolean;
          field_id: string;
          id?: string;
          rule_text: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          enabled?: boolean;
          field_id?: string;
          id?: string;
          rule_text?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "field_rules_field_id_fkey";
            columns: ["field_id"];
            isOneToOne: false;
            referencedRelation: "fields";
            referencedColumns: ["id"];
          },
        ];
      };
      fields: {
        Row: {
          created_at: string;
          enable_individual_confidence_scoring: boolean;
          filter_id: string | null;
          id: string;
          name: string;
          primary_dataset_id: string | null;
          secondary_dataset_id: string | null;
          secondary_filter_id: string | null;
          type: Database["public"]["Enums"]["field_type"];
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          enable_individual_confidence_scoring?: boolean;
          filter_id?: string | null;
          id?: string;
          name: string;
          primary_dataset_id?: string | null;
          secondary_dataset_id?: string | null;
          secondary_filter_id?: string | null;
          type: Database["public"]["Enums"]["field_type"];
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          enable_individual_confidence_scoring?: boolean;
          filter_id?: string | null;
          id?: string;
          name?: string;
          primary_dataset_id?: string | null;
          secondary_dataset_id?: string | null;
          secondary_filter_id?: string | null;
          type?: Database["public"]["Enums"]["field_type"];
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "fields_filter_id_fkey";
            columns: ["filter_id"];
            isOneToOne: false;
            referencedRelation: "filters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "fields_primary_dataset_id_fkey";
            columns: ["primary_dataset_id"];
            isOneToOne: false;
            referencedRelation: "datasets";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "fields_secondary_dataset_id_fkey";
            columns: ["secondary_dataset_id"];
            isOneToOne: false;
            referencedRelation: "datasets";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "fields_secondary_filter_id_fkey";
            columns: ["secondary_filter_id"];
            isOneToOne: false;
            referencedRelation: "filters";
            referencedColumns: ["id"];
          },
        ];
      };
      filter_datapoints: {
        Row: {
          created_at: string;
          datapoint_id: string;
          filter_id: string;
        };
        Insert: {
          created_at?: string;
          datapoint_id: string;
          filter_id: string;
        };
        Update: {
          created_at?: string;
          datapoint_id?: string;
          filter_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "filter_datapoints_datapoint_id_fkey";
            columns: ["datapoint_id"];
            isOneToOne: false;
            referencedRelation: "datapoints";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "filter_datapoints_filter_id_fkey";
            columns: ["filter_id"];
            isOneToOne: false;
            referencedRelation: "filters";
            referencedColumns: ["id"];
          },
        ];
      };
      filters: {
        Row: {
          created_at: string;
          dataset_id: string | null;
          id: string;
          name: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          dataset_id?: string | null;
          id?: string;
          name: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          dataset_id?: string | null;
          id?: string;
          name?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "filters_dataset_id_fkey";
            columns: ["dataset_id"];
            isOneToOne: false;
            referencedRelation: "datasets";
            referencedColumns: ["id"];
          },
        ];
      };
      form_read_corrections: {
        Row: {
          corrected_items: string[];
          created_at: string;
          encounter_id: string;
          extractor_items: string[];
          family_ordinal: number;
          id: string;
          judge_reading_bucket: string | null;
          judge_reading_key: string | null;
          judge_was_right: boolean | null;
          low_confidence: boolean;
          page_bucket: string;
          page_key: string;
          raw_bundle_id: string;
          reason: Database["public"]["Enums"]["form_read_correction_reason"];
          replacement_was_right: boolean | null;
          reviewed_at: string | null;
          reviewed_by: string | null;
          reviewer_answer_bucket: string | null;
          reviewer_answer_key: string | null;
          row_family: string;
          updated_at: string;
        };
        Insert: {
          corrected_items?: string[];
          created_at?: string;
          encounter_id: string;
          extractor_items?: string[];
          family_ordinal: number;
          id?: string;
          judge_reading_bucket?: string | null;
          judge_reading_key?: string | null;
          judge_was_right?: boolean | null;
          low_confidence?: boolean;
          page_bucket: string;
          page_key: string;
          raw_bundle_id: string;
          reason: Database["public"]["Enums"]["form_read_correction_reason"];
          replacement_was_right?: boolean | null;
          reviewed_at?: string | null;
          reviewed_by?: string | null;
          reviewer_answer_bucket?: string | null;
          reviewer_answer_key?: string | null;
          row_family: string;
          updated_at?: string;
        };
        Update: {
          corrected_items?: string[];
          created_at?: string;
          encounter_id?: string;
          extractor_items?: string[];
          family_ordinal?: number;
          id?: string;
          judge_reading_bucket?: string | null;
          judge_reading_key?: string | null;
          judge_was_right?: boolean | null;
          low_confidence?: boolean;
          page_bucket?: string;
          page_key?: string;
          raw_bundle_id?: string;
          reason?: Database["public"]["Enums"]["form_read_correction_reason"];
          replacement_was_right?: boolean | null;
          reviewed_at?: string | null;
          reviewed_by?: string | null;
          reviewer_answer_bucket?: string | null;
          reviewer_answer_key?: string | null;
          row_family?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "form_read_corrections_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "form_read_corrections_raw_bundle_id_fkey";
            columns: ["raw_bundle_id"];
            isOneToOne: false;
            referencedRelation: "raw_files_bundles";
            referencedColumns: ["id"];
          },
        ];
      };
      histology_procedure_verdicts: {
        Row: {
          decided_at: string;
          decided_by: Database["public"]["Enums"]["histology_verdict_decider"];
          histology_document_id: string;
          procedure_document_id: string;
          verdict: Database["public"]["Enums"]["histology_verdict"];
        };
        Insert: {
          decided_at?: string;
          decided_by: Database["public"]["Enums"]["histology_verdict_decider"];
          histology_document_id: string;
          procedure_document_id: string;
          verdict: Database["public"]["Enums"]["histology_verdict"];
        };
        Update: {
          decided_at?: string;
          decided_by?: Database["public"]["Enums"]["histology_verdict_decider"];
          histology_document_id?: string;
          procedure_document_id?: string;
          verdict?: Database["public"]["Enums"]["histology_verdict"];
        };
        Relationships: [
          {
            foreignKeyName: "histology_procedure_verdicts_histology_document_id_fkey";
            columns: ["histology_document_id"];
            isOneToOne: false;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "histology_procedure_verdicts_procedure_document_id_fkey";
            columns: ["procedure_document_id"];
            isOneToOne: false;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
        ];
      };
      hrg_combination_icd10_codes: {
        Row: {
          created_at: string;
          hrg_combination_id: string;
          icd10_code_id: string;
          id: string;
          order_index: number;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          hrg_combination_id: string;
          icd10_code_id: string;
          id?: string;
          order_index: number;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          hrg_combination_id?: string;
          icd10_code_id?: string;
          id?: string;
          order_index?: number;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "hrg_combination_icd10_codes_hrg_combination_id_fkey";
            columns: ["hrg_combination_id"];
            isOneToOne: false;
            referencedRelation: "hrg_combinations";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "hrg_combination_icd10_codes_icd10_code_id_fkey";
            columns: ["icd10_code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
        ];
      };
      hrg_combination_opcs4_codes: {
        Row: {
          created_at: string;
          hrg_combination_id: string;
          id: string;
          opcs4_code_id: string;
          order_index: number;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          hrg_combination_id: string;
          id?: string;
          opcs4_code_id: string;
          order_index: number;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          hrg_combination_id?: string;
          id?: string;
          opcs4_code_id?: string;
          order_index?: number;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "hrg_combination_opcs4_codes_hrg_combination_id_fkey";
            columns: ["hrg_combination_id"];
            isOneToOne: false;
            referencedRelation: "hrg_combinations";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "hrg_combination_opcs4_codes_opcs4_code_id_fkey";
            columns: ["opcs4_code_id"];
            isOneToOne: false;
            referencedRelation: "opcs4_codes";
            referencedColumns: ["id"];
          },
        ];
      };
      hrg_combinations: {
        Row: {
          created_at: string;
          hrg_id: string;
          id: string;
          patient_age: number;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          hrg_id: string;
          id?: string;
          patient_age: number;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          hrg_id?: string;
          id?: string;
          patient_age?: number;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "hrg_combinations_hrg_id_fkey";
            columns: ["hrg_id"];
            isOneToOne: false;
            referencedRelation: "hrgs";
            referencedColumns: ["id"];
          },
        ];
      };
      hrgs: {
        Row: {
          code: string;
          created_at: string;
          description: string | null;
          id: string;
          updated_at: string;
        };
        Insert: {
          code: string;
          created_at?: string;
          description?: string | null;
          id?: string;
          updated_at?: string;
        };
        Update: {
          code?: string;
          created_at?: string;
          description?: string | null;
          id?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      human_review_settings: {
        Row: {
          auto_advance: boolean;
          created_at: string;
          id: string;
          preferred_queue_order: string;
          show_analytics: boolean;
          updated_at: string;
          user_id: string;
        };
        Insert: {
          auto_advance?: boolean;
          created_at?: string;
          id?: string;
          preferred_queue_order?: string;
          show_analytics?: boolean;
          updated_at?: string;
          user_id: string;
        };
        Update: {
          auto_advance?: boolean;
          created_at?: string;
          id?: string;
          preferred_queue_order?: string;
          show_analytics?: boolean;
          updated_at?: string;
          user_id?: string;
        };
        Relationships: [];
      };
      icd_10_co_morbidities_codes: {
        Row: {
          always_code: boolean;
          created_at: string;
          icd_10_code_id: string;
          id: string;
          never_code: boolean;
          updated_at: string;
        };
        Insert: {
          always_code?: boolean;
          created_at?: string;
          icd_10_code_id: string;
          id?: string;
          never_code?: boolean;
          updated_at?: string;
        };
        Update: {
          always_code?: boolean;
          created_at?: string;
          icd_10_code_id?: string;
          id?: string;
          never_code?: boolean;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "icd_10_co_morbidities_codes_icd_10_code_id_fkey";
            columns: ["icd_10_code_id"];
            isOneToOne: true;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
        ];
      };
      icd_10_code_rule_assignments: {
        Row: {
          code_id: string;
          created_at: string;
          id: string;
          rule_id: string;
        };
        Insert: {
          code_id: string;
          created_at?: string;
          id?: string;
          rule_id: string;
        };
        Update: {
          code_id?: string;
          created_at?: string;
          id?: string;
          rule_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "icd_10_code_rule_assignments_code_id_fkey";
            columns: ["code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "icd_10_code_rule_assignments_rule_id_fkey";
            columns: ["rule_id"];
            isOneToOne: false;
            referencedRelation: "icd_10_code_rules";
            referencedColumns: ["id"];
          },
        ];
      };
      icd_10_code_rule_customers: {
        Row: {
          created_at: string;
          customer_id: string;
          id: string;
          rule_id: string;
        };
        Insert: {
          created_at?: string;
          customer_id: string;
          id?: string;
          rule_id: string;
        };
        Update: {
          created_at?: string;
          customer_id?: string;
          id?: string;
          rule_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "icd_10_code_rule_customers_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "icd_10_code_rule_customers_rule_id_fkey";
            columns: ["rule_id"];
            isOneToOne: false;
            referencedRelation: "icd_10_code_rules";
            referencedColumns: ["id"];
          },
        ];
      };
      icd_10_code_rule_specialties: {
        Row: {
          created_at: string;
          id: string;
          rule_id: string;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"];
        };
        Insert: {
          created_at?: string;
          id?: string;
          rule_id: string;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"];
        };
        Update: {
          created_at?: string;
          id?: string;
          rule_id?: string;
          specialty?: Database["public"]["Enums"]["encounter_speciality_type"];
        };
        Relationships: [
          {
            foreignKeyName: "icd_10_code_rule_specialties_rule_id_fkey";
            columns: ["rule_id"];
            isOneToOne: false;
            referencedRelation: "icd_10_code_rules";
            referencedColumns: ["id"];
          },
        ];
      };
      icd_10_code_rules: {
        Row: {
          created_at: string;
          id: string;
          is_active: boolean;
          name: string;
          rule_text: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          is_active?: boolean;
          name: string;
          rule_text: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          is_active?: boolean;
          name?: string;
          rule_text?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      icd_10_codes_vectorised: {
        Row: {
          created_at: string | null;
          description: string | null;
          description_embedding: string | null;
          gender_mask: string | null;
          icd10_codes_id: string;
          id: string;
          max_age: number | null;
          min_age: number | null;
          modifier_4: string | null;
          modifier_5: string | null;
          qualifiers: string | null;
          tree_description: string | null;
        };
        Insert: {
          created_at?: string | null;
          description?: string | null;
          description_embedding?: string | null;
          gender_mask?: string | null;
          icd10_codes_id: string;
          id?: string;
          max_age?: number | null;
          min_age?: number | null;
          modifier_4?: string | null;
          modifier_5?: string | null;
          qualifiers?: string | null;
          tree_description?: string | null;
        };
        Update: {
          created_at?: string | null;
          description?: string | null;
          description_embedding?: string | null;
          gender_mask?: string | null;
          icd10_codes_id?: string;
          id?: string;
          max_age?: number | null;
          min_age?: number | null;
          modifier_4?: string | null;
          modifier_5?: string | null;
          qualifiers?: string | null;
          tree_description?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "fk_icd10_codes_id";
            columns: ["icd10_codes_id"];
            isOneToOne: true;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "icd_10_codes_vectorised_icd10_codes_id_fkey";
            columns: ["icd10_codes_id"];
            isOneToOne: true;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
        ];
      };
      icd_10_coding_standards_5th_edition: {
        Row: {
          chunk_index: number | null;
          created_at: string | null;
          embedding: string | null;
          heading: string | null;
          id: string;
          page: number | null;
          section: string | null;
          text: string;
        };
        Insert: {
          chunk_index?: number | null;
          created_at?: string | null;
          embedding?: string | null;
          heading?: string | null;
          id?: string;
          page?: number | null;
          section?: string | null;
          text: string;
        };
        Update: {
          chunk_index?: number | null;
          created_at?: string | null;
          embedding?: string | null;
          heading?: string | null;
          id?: string;
          page?: number | null;
          section?: string | null;
          text?: string;
        };
        Relationships: [];
      };
      icd10_code_bundles: {
        Row: {
          created_at: string;
          id: string;
          primary_code_id: string;
          secondary_code_id: string;
          selection_hint: string | null;
          source_text: string | null;
          type: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          primary_code_id: string;
          secondary_code_id: string;
          selection_hint?: string | null;
          source_text?: string | null;
          type?: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          primary_code_id?: string;
          secondary_code_id?: string;
          selection_hint?: string | null;
          source_text?: string | null;
          type?: string;
        };
        Relationships: [
          {
            foreignKeyName: "icd10_code_bundles_primary_code_id_icd10_codes_id_fk";
            columns: ["primary_code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "icd10_code_bundles_secondary_code_id_icd10_codes_id_fk";
            columns: ["secondary_code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
        ];
      };
      icd10_codes: {
        Row: {
          alt_code: string | null;
          code: string;
          created_at: string;
          description: string;
          frequency_pct: number | null;
          gender_restriction: Database["public"]["Enums"]["icd10_gender_restriction"] | null;
          id: string;
          last_updated_at: string | null;
          last_updated_description: string | null;
          max_age: number | null;
          min_age: number | null;
          updated_at: string;
        };
        Insert: {
          alt_code?: string | null;
          code: string;
          created_at?: string;
          description: string;
          frequency_pct?: number | null;
          gender_restriction?: Database["public"]["Enums"]["icd10_gender_restriction"] | null;
          id?: string;
          last_updated_at?: string | null;
          last_updated_description?: string | null;
          max_age?: number | null;
          min_age?: number | null;
          updated_at?: string;
        };
        Update: {
          alt_code?: string | null;
          code?: string;
          created_at?: string;
          description?: string;
          frequency_pct?: number | null;
          gender_restriction?: Database["public"]["Enums"]["icd10_gender_restriction"] | null;
          id?: string;
          last_updated_at?: string | null;
          last_updated_description?: string | null;
          max_age?: number | null;
          min_age?: number | null;
          updated_at?: string;
        };
        Relationships: [];
      };
      icd10_index_code_families: {
        Row: {
          description: string | null;
          fourth_char: string | null;
          id: string;
          prefix: string | null;
          range_end: string | null;
          range_start: string | null;
          type: Database["public"]["Enums"]["icd_index_code_family_type"];
        };
        Insert: {
          description?: string | null;
          fourth_char?: string | null;
          id?: string;
          prefix?: string | null;
          range_end?: string | null;
          range_start?: string | null;
          type: Database["public"]["Enums"]["icd_index_code_family_type"];
        };
        Update: {
          description?: string | null;
          fourth_char?: string | null;
          id?: string;
          prefix?: string | null;
          range_end?: string | null;
          range_start?: string | null;
          type?: Database["public"]["Enums"]["icd_index_code_family_type"];
        };
        Relationships: [];
      };
      icd10_index_rules: {
        Row: {
          active: boolean;
          applies_to: string;
          code_id: string | null;
          created_at: string;
          edge_id: string | null;
          family_id: string | null;
          id: string;
          note: string | null;
          pattern: Json | null;
          severity: Database["public"]["Enums"]["icd_index_rule_severity"];
          term_id: string | null;
          type: Database["public"]["Enums"]["icd_index_rule_type"];
        };
        Insert: {
          active?: boolean;
          applies_to: string;
          code_id?: string | null;
          created_at?: string;
          edge_id?: string | null;
          family_id?: string | null;
          id?: string;
          note?: string | null;
          pattern?: Json | null;
          severity: Database["public"]["Enums"]["icd_index_rule_severity"];
          term_id?: string | null;
          type: Database["public"]["Enums"]["icd_index_rule_type"];
        };
        Update: {
          active?: boolean;
          applies_to?: string;
          code_id?: string | null;
          created_at?: string;
          edge_id?: string | null;
          family_id?: string | null;
          id?: string;
          note?: string | null;
          pattern?: Json | null;
          severity?: Database["public"]["Enums"]["icd_index_rule_severity"];
          term_id?: string | null;
          type?: Database["public"]["Enums"]["icd_index_rule_type"];
        };
        Relationships: [
          {
            foreignKeyName: "icd10_index_rules_code_id_icd10_codes_id_fk";
            columns: ["code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "icd10_index_rules_edge_id_icd10_index_term_edges_id_fk";
            columns: ["edge_id"];
            isOneToOne: false;
            referencedRelation: "icd10_index_term_edges";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "icd10_index_rules_family_id_icd10_index_code_families_id_fk";
            columns: ["family_id"];
            isOneToOne: false;
            referencedRelation: "icd10_index_code_families";
            referencedColumns: ["id"];
          },
        ];
      };
      icd10_index_sources: {
        Row: {
          created_at: string;
          file_name: string;
          id: string;
          revision: string | null;
          section: Database["public"]["Enums"]["icd_index_section"];
        };
        Insert: {
          created_at?: string;
          file_name: string;
          id?: string;
          revision?: string | null;
          section: Database["public"]["Enums"]["icd_index_section"];
        };
        Update: {
          created_at?: string;
          file_name?: string;
          id?: string;
          revision?: string | null;
          section?: Database["public"]["Enums"]["icd_index_section"];
        };
        Relationships: [];
      };
      icd10_index_term_code_families: {
        Row: {
          family_id: string;
          id: string;
          note: string | null;
          role: Database["public"]["Enums"]["icd_index_code_role"];
          term_id: string;
        };
        Insert: {
          family_id: string;
          id?: string;
          note?: string | null;
          role?: Database["public"]["Enums"]["icd_index_code_role"];
          term_id: string;
        };
        Update: {
          family_id?: string;
          id?: string;
          note?: string | null;
          role?: Database["public"]["Enums"]["icd_index_code_role"];
          term_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "icd10_index_term_code_families_family_id_icd10_index_code_famil";
            columns: ["family_id"];
            isOneToOne: false;
            referencedRelation: "icd10_index_code_families";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "icd10_index_term_code_families_term_id_icd10_index_terms_id_fk";
            columns: ["term_id"];
            isOneToOne: false;
            referencedRelation: "icd10_index_terms";
            referencedColumns: ["id"];
          },
        ];
      };
      icd10_index_term_codes: {
        Row: {
          bundle_id: string | null;
          code_id: string;
          id: string;
          note: string | null;
          role: Database["public"]["Enums"]["icd_index_code_role"];
          source_frag: string | null;
          term_id: string;
        };
        Insert: {
          bundle_id?: string | null;
          code_id: string;
          id?: string;
          note?: string | null;
          role?: Database["public"]["Enums"]["icd_index_code_role"];
          source_frag?: string | null;
          term_id: string;
        };
        Update: {
          bundle_id?: string | null;
          code_id?: string;
          id?: string;
          note?: string | null;
          role?: Database["public"]["Enums"]["icd_index_code_role"];
          source_frag?: string | null;
          term_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "icd10_index_term_codes_code_id_icd10_codes_id_fk";
            columns: ["code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "icd10_index_term_codes_term_id_icd10_index_terms_id_fk";
            columns: ["term_id"];
            isOneToOne: false;
            referencedRelation: "icd10_index_terms";
            referencedColumns: ["id"];
          },
        ];
      };
      icd10_index_term_edges: {
        Row: {
          depth: number | null;
          edge_type: Database["public"]["Enums"]["icd_index_edge_type"];
          from_term_id: string;
          id: string;
          meta: Json | null;
          to_term_id: string | null;
        };
        Insert: {
          depth?: number | null;
          edge_type: Database["public"]["Enums"]["icd_index_edge_type"];
          from_term_id: string;
          id?: string;
          meta?: Json | null;
          to_term_id?: string | null;
        };
        Update: {
          depth?: number | null;
          edge_type?: Database["public"]["Enums"]["icd_index_edge_type"];
          from_term_id?: string;
          id?: string;
          meta?: Json | null;
          to_term_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "icd10_index_term_edges_from_term_id_icd10_index_terms_id_fk";
            columns: ["from_term_id"];
            isOneToOne: false;
            referencedRelation: "icd10_index_terms";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "icd10_index_term_edges_to_term_id_icd10_index_terms_id_fk";
            columns: ["to_term_id"];
            isOneToOne: false;
            referencedRelation: "icd10_index_terms";
            referencedColumns: ["id"];
          },
        ];
      };
      icd10_index_term_tags: {
        Row: {
          tag: Database["public"]["Enums"]["icd_index_tag"];
          term_id: string;
        };
        Insert: {
          tag: Database["public"]["Enums"]["icd_index_tag"];
          term_id: string;
        };
        Update: {
          tag?: Database["public"]["Enums"]["icd_index_tag"];
          term_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "icd10_index_term_tags_term_id_icd10_index_terms_id_fk";
            columns: ["term_id"];
            isOneToOne: false;
            referencedRelation: "icd10_index_terms";
            referencedColumns: ["id"];
          },
        ];
      };
      icd10_index_term_tokens: {
        Row: {
          term_id: string;
          token: string;
        };
        Insert: {
          term_id: string;
          token: string;
        };
        Update: {
          term_id?: string;
          token?: string;
        };
        Relationships: [
          {
            foreignKeyName: "icd10_index_term_tokens_term_id_icd10_index_terms_id_fk";
            columns: ["term_id"];
            isOneToOne: false;
            referencedRelation: "icd10_index_terms";
            referencedColumns: ["id"];
          },
        ];
      };
      icd10_index_terms: {
        Row: {
          anchor_id: string;
          canonical_text: string;
          created_at: string;
          id: string;
          is_main: boolean;
          key_term: boolean;
          level: number;
          meta: Json | null;
          norm_text: string;
          norm_tsv: unknown;
          parentheses: Json | null;
          section: Database["public"]["Enums"]["icd_index_section"];
          source_id: string;
          text: string;
        };
        Insert: {
          anchor_id: string;
          canonical_text: string;
          created_at?: string;
          id?: string;
          is_main?: boolean;
          key_term?: boolean;
          level?: number;
          meta?: Json | null;
          norm_text: string;
          norm_tsv?: unknown;
          parentheses?: Json | null;
          section: Database["public"]["Enums"]["icd_index_section"];
          source_id: string;
          text: string;
        };
        Update: {
          anchor_id?: string;
          canonical_text?: string;
          created_at?: string;
          id?: string;
          is_main?: boolean;
          key_term?: boolean;
          level?: number;
          meta?: Json | null;
          norm_text?: string;
          norm_tsv?: unknown;
          parentheses?: Json | null;
          section?: Database["public"]["Enums"]["icd_index_section"];
          source_id?: string;
          text?: string;
        };
        Relationships: [
          {
            foreignKeyName: "icd10_index_terms_source_id_icd10_index_sources_id_fk";
            columns: ["source_id"];
            isOneToOne: false;
            referencedRelation: "icd10_index_sources";
            referencedColumns: ["id"];
          },
        ];
      };
      inbound_classification_debug_info: {
        Row: {
          created_at: string;
          debug: Json;
          id: string;
        };
        Insert: {
          created_at?: string;
          debug: Json;
          id?: string;
        };
        Update: {
          created_at?: string;
          debug?: Json;
          id?: string;
        };
        Relationships: [];
      };
      inbound_classification_files: {
        Row: {
          bucket: string;
          created_at: string | null;
          id: string;
          inbound_classification_result_id: string;
          object_key: string;
        };
        Insert: {
          bucket: string;
          created_at?: string | null;
          id?: string;
          inbound_classification_result_id: string;
          object_key: string;
        };
        Update: {
          bucket?: string;
          created_at?: string | null;
          id?: string;
          inbound_classification_result_id?: string;
          object_key?: string;
        };
        Relationships: [
          {
            foreignKeyName: "inbound_classification_files_inbound_classification_result_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_files_inbound_classification_result_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_files_inbound_classification_result_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_files_inbound_classification_result_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "inbound_classification_files_inbound_classification_result_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_files_inbound_classification_result_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
        ];
      };
      inbound_classification_result_diagnosis: {
        Row: {
          created_at: string;
          icd_10_code: string;
          id: string;
          inbound_classification_result_id: string;
          is_comorbidity: boolean;
          is_historic_code: boolean;
          is_optional: boolean;
          order_index: number;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          icd_10_code: string;
          id?: string;
          inbound_classification_result_id: string;
          is_comorbidity?: boolean;
          is_historic_code?: boolean;
          is_optional?: boolean;
          order_index: number;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          icd_10_code?: string;
          id?: string;
          inbound_classification_result_id?: string;
          is_comorbidity?: boolean;
          is_historic_code?: boolean;
          is_optional?: boolean;
          order_index?: number;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "inbound_classification_result_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_result_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_result_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_result_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "inbound_classification_result_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_result_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
        ];
      };
      inbound_classification_result_procedures: {
        Row: {
          ai_flag_text: string | null;
          created_at: string;
          id: string;
          inbound_classification_result_id: string;
          is_optional: boolean;
          opcs_4_code: string;
          opcs_4_version: number;
          order_index: number;
          updated_at: string;
        };
        Insert: {
          ai_flag_text?: string | null;
          created_at?: string;
          id?: string;
          inbound_classification_result_id: string;
          is_optional?: boolean;
          opcs_4_code: string;
          opcs_4_version?: number;
          order_index: number;
          updated_at?: string;
        };
        Update: {
          ai_flag_text?: string | null;
          created_at?: string;
          id?: string;
          inbound_classification_result_id?: string;
          is_optional?: boolean;
          opcs_4_code?: string;
          opcs_4_version?: number;
          order_index?: number;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "inbound_classification_resul_inbound_classification_resul_fkey1";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_resul_inbound_classification_resul_fkey1";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_resul_inbound_classification_resul_fkey1";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_resul_inbound_classification_resul_fkey1";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "inbound_classification_resul_inbound_classification_resul_fkey1";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_classification_resul_inbound_classification_resul_fkey1";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
        ];
      };
      inbound_classification_results: {
        Row: {
          ai_flag_for_review: boolean;
          ai_flag_for_review_text: string | null;
          classified_at: string;
          created_at: string;
          customer_id: string;
          encounter_id: string;
          error_message: string | null;
          hrg_id: string | null;
          human_accepted: string | null;
          human_flag_for_review: boolean;
          human_flag_for_review_text: string | null;
          id: string;
          is_follow_up: boolean;
          langfuse_trace_id: string | null;
          outcome: Database["public"]["Enums"]["classification_result_outcome"];
          patient_id: string;
          run_id: string | null;
          run_origin: Database["public"]["Enums"]["classification_run_origin"];
          triggered_by_user_id: string | null;
          updated_at: string;
        };
        Insert: {
          ai_flag_for_review?: boolean;
          ai_flag_for_review_text?: string | null;
          classified_at?: string;
          created_at?: string;
          customer_id: string;
          encounter_id: string;
          error_message?: string | null;
          hrg_id?: string | null;
          human_accepted?: string | null;
          human_flag_for_review?: boolean;
          human_flag_for_review_text?: string | null;
          id?: string;
          is_follow_up?: boolean;
          langfuse_trace_id?: string | null;
          outcome: Database["public"]["Enums"]["classification_result_outcome"];
          patient_id: string;
          run_id?: string | null;
          run_origin?: Database["public"]["Enums"]["classification_run_origin"];
          triggered_by_user_id?: string | null;
          updated_at?: string;
        };
        Update: {
          ai_flag_for_review?: boolean;
          ai_flag_for_review_text?: string | null;
          classified_at?: string;
          created_at?: string;
          customer_id?: string;
          encounter_id?: string;
          error_message?: string | null;
          hrg_id?: string | null;
          human_accepted?: string | null;
          human_flag_for_review?: boolean;
          human_flag_for_review_text?: string | null;
          id?: string;
          is_follow_up?: boolean;
          langfuse_trace_id?: string | null;
          outcome?: Database["public"]["Enums"]["classification_result_outcome"];
          patient_id?: string;
          run_id?: string | null;
          run_origin?: Database["public"]["Enums"]["classification_run_origin"];
          triggered_by_user_id?: string | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "inbound_classification_results_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_hrg_id_fkey";
            columns: ["hrg_id"];
            isOneToOne: false;
            referencedRelation: "hrgs";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "inbound_classification_results_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      inbound_customer_buckets: {
        Row: {
          bucket: string;
          created_at: string;
          customer_id: string;
          id: string;
        };
        Insert: {
          bucket: string;
          created_at?: string;
          customer_id: string;
          id?: string;
        };
        Update: {
          bucket?: string;
          created_at?: string;
          customer_id?: string;
          id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "inbound_customer_buckets_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: true;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
        ];
      };
      inbound_endoscopy_specific_results: {
        Row: {
          created_at: string;
          id: string;
          inbound_classification_result_id: string;
          observation_identifier: string | null;
          primary_diagnosis_identified: boolean | null;
          updated_at: string;
          was_specimen_or_histology_taken: boolean;
        };
        Insert: {
          created_at?: string;
          id?: string;
          inbound_classification_result_id: string;
          observation_identifier?: string | null;
          primary_diagnosis_identified?: boolean | null;
          updated_at?: string;
          was_specimen_or_histology_taken?: boolean;
        };
        Update: {
          created_at?: string;
          id?: string;
          inbound_classification_result_id?: string;
          observation_identifier?: string | null;
          primary_diagnosis_identified?: boolean | null;
          updated_at?: string;
          was_specimen_or_histology_taken?: boolean;
        };
        Relationships: [
          {
            foreignKeyName: "inbound_endoscopy_specific_re_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_endoscopy_specific_re_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_endoscopy_specific_re_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: true;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_endoscopy_specific_re_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: true;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "inbound_endoscopy_specific_re_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: true;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "inbound_endoscopy_specific_re_inbound_classification_resul_fkey";
            columns: ["inbound_classification_result_id"];
            isOneToOne: true;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
        ];
      };
      inbound_pipeline_idempotency: {
        Row: {
          error: string | null;
          id: string;
          message_id: string;
          processed_at: string;
          result_hash: string | null;
          s3_key: string;
          status: string;
        };
        Insert: {
          error?: string | null;
          id: string;
          message_id: string;
          processed_at?: string;
          result_hash?: string | null;
          s3_key: string;
          status: string;
        };
        Update: {
          error?: string | null;
          id?: string;
          message_id?: string;
          processed_at?: string;
          result_hash?: string | null;
          s3_key?: string;
          status?: string;
        };
        Relationships: [];
      };
      infusion_appointment_document_data: {
        Row: {
          appointment_id: string;
          created_at: string;
          document_id: string;
          observation_datetime: string;
          updated_at: string;
        };
        Insert: {
          appointment_id: string;
          created_at?: string;
          document_id: string;
          observation_datetime: string;
          updated_at?: string;
        };
        Update: {
          appointment_id?: string;
          created_at?: string;
          document_id?: string;
          observation_datetime?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "infusion_appointment_document_data_document_id_fkey";
            columns: ["document_id"];
            isOneToOne: true;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
        ];
      };
      mastra_agent_versions: {
        Row: {
          agentId: string;
          agents: Json | null;
          browser: Json | null;
          changedFields: Json | null;
          changeMessage: string | null;
          createdAt: string;
          createdAtZ: string | null;
          defaultOptions: Json | null;
          description: string | null;
          durable: Json | null;
          id: string;
          inputProcessors: Json | null;
          instructions: string;
          integrationTools: Json | null;
          mcpClients: Json | null;
          memory: Json | null;
          model: Json;
          name: string;
          outputProcessors: Json | null;
          requestContextSchema: Json | null;
          scorers: Json | null;
          skills: Json | null;
          skillsFormat: string | null;
          toolProviders: Json | null;
          tools: Json | null;
          versionNumber: number;
          workflows: Json | null;
          workspace: Json | null;
        };
        Insert: {
          agentId: string;
          agents?: Json | null;
          browser?: Json | null;
          changedFields?: Json | null;
          changeMessage?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          defaultOptions?: Json | null;
          description?: string | null;
          durable?: Json | null;
          id: string;
          inputProcessors?: Json | null;
          instructions: string;
          integrationTools?: Json | null;
          mcpClients?: Json | null;
          memory?: Json | null;
          model: Json;
          name: string;
          outputProcessors?: Json | null;
          requestContextSchema?: Json | null;
          scorers?: Json | null;
          skills?: Json | null;
          skillsFormat?: string | null;
          toolProviders?: Json | null;
          tools?: Json | null;
          versionNumber: number;
          workflows?: Json | null;
          workspace?: Json | null;
        };
        Update: {
          agentId?: string;
          agents?: Json | null;
          browser?: Json | null;
          changedFields?: Json | null;
          changeMessage?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          defaultOptions?: Json | null;
          description?: string | null;
          durable?: Json | null;
          id?: string;
          inputProcessors?: Json | null;
          instructions?: string;
          integrationTools?: Json | null;
          mcpClients?: Json | null;
          memory?: Json | null;
          model?: Json;
          name?: string;
          outputProcessors?: Json | null;
          requestContextSchema?: Json | null;
          scorers?: Json | null;
          skills?: Json | null;
          skillsFormat?: string | null;
          toolProviders?: Json | null;
          tools?: Json | null;
          versionNumber?: number;
          workflows?: Json | null;
          workspace?: Json | null;
        };
        Relationships: [];
      };
      mastra_agents: {
        Row: {
          activeVersionId: string | null;
          authorId: string | null;
          createdAt: string;
          createdAtZ: string | null;
          favoriteCount: number | null;
          id: string;
          metadata: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ: string | null;
          visibility: string | null;
        };
        Insert: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          favoriteCount?: number | null;
          id: string;
          metadata?: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ?: string | null;
          visibility?: string | null;
        };
        Update: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          favoriteCount?: number | null;
          id?: string;
          metadata?: Json | null;
          status?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
          visibility?: string | null;
        };
        Relationships: [];
      };
      mastra_ai_spans: {
        Row: {
          attributes: Json | null;
          createdAt: string;
          createdAtZ: string | null;
          endedAt: string | null;
          endedAtZ: string | null;
          entityId: string | null;
          entityName: string | null;
          entityType: string | null;
          entityVersionId: string | null;
          environment: string | null;
          error: Json | null;
          experimentId: string | null;
          input: Json | null;
          isEvent: boolean;
          links: Json | null;
          metadata: Json | null;
          name: string;
          organizationId: string | null;
          output: Json | null;
          parentEntityId: string | null;
          parentEntityName: string | null;
          parentEntityType: string | null;
          parentEntityVersionId: string | null;
          parentSpanId: string | null;
          requestContext: Json | null;
          requestId: string | null;
          resourceId: string | null;
          rootEntityId: string | null;
          rootEntityName: string | null;
          rootEntityType: string | null;
          rootEntityVersionId: string | null;
          runId: string | null;
          scope: Json | null;
          serviceName: string | null;
          sessionId: string | null;
          source: string | null;
          spanId: string;
          spanType: string;
          startedAt: string;
          startedAtZ: string | null;
          tags: Json | null;
          threadId: string | null;
          traceId: string;
          updatedAt: string | null;
          updatedAtZ: string | null;
          userId: string | null;
        };
        Insert: {
          attributes?: Json | null;
          createdAt: string;
          createdAtZ?: string | null;
          endedAt?: string | null;
          endedAtZ?: string | null;
          entityId?: string | null;
          entityName?: string | null;
          entityType?: string | null;
          entityVersionId?: string | null;
          environment?: string | null;
          error?: Json | null;
          experimentId?: string | null;
          input?: Json | null;
          isEvent: boolean;
          links?: Json | null;
          metadata?: Json | null;
          name: string;
          organizationId?: string | null;
          output?: Json | null;
          parentEntityId?: string | null;
          parentEntityName?: string | null;
          parentEntityType?: string | null;
          parentEntityVersionId?: string | null;
          parentSpanId?: string | null;
          requestContext?: Json | null;
          requestId?: string | null;
          resourceId?: string | null;
          rootEntityId?: string | null;
          rootEntityName?: string | null;
          rootEntityType?: string | null;
          rootEntityVersionId?: string | null;
          runId?: string | null;
          scope?: Json | null;
          serviceName?: string | null;
          sessionId?: string | null;
          source?: string | null;
          spanId: string;
          spanType: string;
          startedAt: string;
          startedAtZ?: string | null;
          tags?: Json | null;
          threadId?: string | null;
          traceId: string;
          updatedAt?: string | null;
          updatedAtZ?: string | null;
          userId?: string | null;
        };
        Update: {
          attributes?: Json | null;
          createdAt?: string;
          createdAtZ?: string | null;
          endedAt?: string | null;
          endedAtZ?: string | null;
          entityId?: string | null;
          entityName?: string | null;
          entityType?: string | null;
          entityVersionId?: string | null;
          environment?: string | null;
          error?: Json | null;
          experimentId?: string | null;
          input?: Json | null;
          isEvent?: boolean;
          links?: Json | null;
          metadata?: Json | null;
          name?: string;
          organizationId?: string | null;
          output?: Json | null;
          parentEntityId?: string | null;
          parentEntityName?: string | null;
          parentEntityType?: string | null;
          parentEntityVersionId?: string | null;
          parentSpanId?: string | null;
          requestContext?: Json | null;
          requestId?: string | null;
          resourceId?: string | null;
          rootEntityId?: string | null;
          rootEntityName?: string | null;
          rootEntityType?: string | null;
          rootEntityVersionId?: string | null;
          runId?: string | null;
          scope?: Json | null;
          serviceName?: string | null;
          sessionId?: string | null;
          source?: string | null;
          spanId?: string;
          spanType?: string;
          startedAt?: string;
          startedAtZ?: string | null;
          tags?: Json | null;
          threadId?: string | null;
          traceId?: string;
          updatedAt?: string | null;
          updatedAtZ?: string | null;
          userId?: string | null;
        };
        Relationships: [];
      };
      mastra_background_tasks: {
        Row: {
          agent_id: string;
          args: Json;
          completedAt: string | null;
          completedAtZ: string | null;
          createdAt: string;
          createdAtZ: string | null;
          error: Json | null;
          id: string;
          leaseExpiresAt: string | null;
          leaseExpiresAtZ: string | null;
          max_retries: number;
          ownerId: string | null;
          resource_id: string | null;
          result: Json | null;
          retry_count: number;
          run_id: string;
          startedAt: string | null;
          startedAtZ: string | null;
          status: string;
          suspend_payload: Json | null;
          suspendedAt: string | null;
          suspendedAtZ: string | null;
          thread_id: string | null;
          timeout_ms: number;
          tool_call_id: string;
          tool_name: string;
        };
        Insert: {
          agent_id: string;
          args: Json;
          completedAt?: string | null;
          completedAtZ?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          error?: Json | null;
          id: string;
          leaseExpiresAt?: string | null;
          leaseExpiresAtZ?: string | null;
          max_retries: number;
          ownerId?: string | null;
          resource_id?: string | null;
          result?: Json | null;
          retry_count: number;
          run_id: string;
          startedAt?: string | null;
          startedAtZ?: string | null;
          status: string;
          suspend_payload?: Json | null;
          suspendedAt?: string | null;
          suspendedAtZ?: string | null;
          thread_id?: string | null;
          timeout_ms: number;
          tool_call_id: string;
          tool_name: string;
        };
        Update: {
          agent_id?: string;
          args?: Json;
          completedAt?: string | null;
          completedAtZ?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          error?: Json | null;
          id?: string;
          leaseExpiresAt?: string | null;
          leaseExpiresAtZ?: string | null;
          max_retries?: number;
          ownerId?: string | null;
          resource_id?: string | null;
          result?: Json | null;
          retry_count?: number;
          run_id?: string;
          startedAt?: string | null;
          startedAtZ?: string | null;
          status?: string;
          suspend_payload?: Json | null;
          suspendedAt?: string | null;
          suspendedAtZ?: string | null;
          thread_id?: string | null;
          timeout_ms?: number;
          tool_call_id?: string;
          tool_name?: string;
        };
        Relationships: [];
      };
      mastra_channel_config: {
        Row: {
          data: Json;
          platform: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          data: Json;
          platform: string;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          data?: Json;
          platform?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_channel_installations: {
        Row: {
          agentId: string;
          configHash: string | null;
          createdAt: string;
          createdAtZ: string | null;
          data: Json;
          error: string | null;
          id: string;
          platform: string;
          status: string;
          updatedAt: string;
          updatedAtZ: string | null;
          webhookId: string | null;
        };
        Insert: {
          agentId: string;
          configHash?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          data: Json;
          error?: string | null;
          id: string;
          platform: string;
          status: string;
          updatedAt: string;
          updatedAtZ?: string | null;
          webhookId?: string | null;
        };
        Update: {
          agentId?: string;
          configHash?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          data?: Json;
          error?: string | null;
          id?: string;
          platform?: string;
          status?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
          webhookId?: string | null;
        };
        Relationships: [];
      };
      mastra_dataset_items: {
        Row: {
          createdAt: string;
          createdAtZ: string | null;
          datasetId: string;
          datasetVersion: number;
          expectedTrajectory: Json | null;
          externalId: string | null;
          groundTruth: Json | null;
          id: string;
          input: Json;
          isDeleted: boolean;
          metadata: Json | null;
          organizationId: string | null;
          projectId: string | null;
          requestContext: Json | null;
          scorerIds: Json | null;
          source: Json | null;
          toolMocks: Json | null;
          unmockedToolPolicy: string | null;
          updatedAt: string;
          updatedAtZ: string | null;
          validTo: number | null;
        };
        Insert: {
          createdAt: string;
          createdAtZ?: string | null;
          datasetId: string;
          datasetVersion: number;
          expectedTrajectory?: Json | null;
          externalId?: string | null;
          groundTruth?: Json | null;
          id: string;
          input: Json;
          isDeleted: boolean;
          metadata?: Json | null;
          organizationId?: string | null;
          projectId?: string | null;
          requestContext?: Json | null;
          scorerIds?: Json | null;
          source?: Json | null;
          toolMocks?: Json | null;
          unmockedToolPolicy?: string | null;
          updatedAt: string;
          updatedAtZ?: string | null;
          validTo?: number | null;
        };
        Update: {
          createdAt?: string;
          createdAtZ?: string | null;
          datasetId?: string;
          datasetVersion?: number;
          expectedTrajectory?: Json | null;
          externalId?: string | null;
          groundTruth?: Json | null;
          id?: string;
          input?: Json;
          isDeleted?: boolean;
          metadata?: Json | null;
          organizationId?: string | null;
          projectId?: string | null;
          requestContext?: Json | null;
          scorerIds?: Json | null;
          source?: Json | null;
          toolMocks?: Json | null;
          unmockedToolPolicy?: string | null;
          updatedAt?: string;
          updatedAtZ?: string | null;
          validTo?: number | null;
        };
        Relationships: [];
      };
      mastra_dataset_versions: {
        Row: {
          createdAt: string;
          createdAtZ: string | null;
          datasetId: string;
          id: string;
          version: number;
        };
        Insert: {
          createdAt: string;
          createdAtZ?: string | null;
          datasetId: string;
          id: string;
          version: number;
        };
        Update: {
          createdAt?: string;
          createdAtZ?: string | null;
          datasetId?: string;
          id?: string;
          version?: number;
        };
        Relationships: [];
      };
      mastra_datasets: {
        Row: {
          candidateId: string | null;
          candidateKey: string | null;
          createdAt: string;
          createdAtZ: string | null;
          description: string | null;
          groundTruthSchema: Json | null;
          id: string;
          inputSchema: Json | null;
          metadata: Json | null;
          name: string;
          organizationId: string | null;
          projectId: string | null;
          requestContextSchema: Json | null;
          scorerIds: Json | null;
          tags: Json | null;
          targetIds: Json | null;
          targetType: string | null;
          updatedAt: string;
          updatedAtZ: string | null;
          version: number;
        };
        Insert: {
          candidateId?: string | null;
          candidateKey?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          description?: string | null;
          groundTruthSchema?: Json | null;
          id: string;
          inputSchema?: Json | null;
          metadata?: Json | null;
          name: string;
          organizationId?: string | null;
          projectId?: string | null;
          requestContextSchema?: Json | null;
          scorerIds?: Json | null;
          tags?: Json | null;
          targetIds?: Json | null;
          targetType?: string | null;
          updatedAt: string;
          updatedAtZ?: string | null;
          version: number;
        };
        Update: {
          candidateId?: string | null;
          candidateKey?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          description?: string | null;
          groundTruthSchema?: Json | null;
          id?: string;
          inputSchema?: Json | null;
          metadata?: Json | null;
          name?: string;
          organizationId?: string | null;
          projectId?: string | null;
          requestContextSchema?: Json | null;
          scorerIds?: Json | null;
          tags?: Json | null;
          targetIds?: Json | null;
          targetType?: string | null;
          updatedAt?: string;
          updatedAtZ?: string | null;
          version?: number;
        };
        Relationships: [];
      };
      mastra_evals: {
        Row: {
          agent_name: string;
          created_at: string | null;
          created_atZ: string | null;
          createdAt: string | null;
          createdAtZ: string | null;
          global_run_id: string;
          input: string;
          instructions: string;
          metric_name: string;
          output: string;
          result: Json;
          run_id: string;
          test_info: Json | null;
        };
        Insert: {
          agent_name: string;
          created_at?: string | null;
          created_atZ?: string | null;
          createdAt?: string | null;
          createdAtZ?: string | null;
          global_run_id: string;
          input: string;
          instructions: string;
          metric_name: string;
          output: string;
          result: Json;
          run_id: string;
          test_info?: Json | null;
        };
        Update: {
          agent_name?: string;
          created_at?: string | null;
          created_atZ?: string | null;
          createdAt?: string | null;
          createdAtZ?: string | null;
          global_run_id?: string;
          input?: string;
          instructions?: string;
          metric_name?: string;
          output?: string;
          result?: Json;
          run_id?: string;
          test_info?: Json | null;
        };
        Relationships: [];
      };
      mastra_experiment_results: {
        Row: {
          attempt: number | null;
          comment: string | null;
          completedAt: string;
          completedAtZ: string | null;
          createdAt: string;
          createdAtZ: string | null;
          error: Json | null;
          experimentId: string;
          groundTruth: Json | null;
          id: string;
          input: Json;
          itemDatasetVersion: number | null;
          itemId: string;
          metadata: Json | null;
          organizationId: string | null;
          output: Json | null;
          projectId: string | null;
          retryCount: number;
          startedAt: string;
          startedAtZ: string | null;
          status: string | null;
          tags: Json | null;
          toolMockReport: Json | null;
          traceId: string | null;
        };
        Insert: {
          attempt?: number | null;
          comment?: string | null;
          completedAt: string;
          completedAtZ?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          error?: Json | null;
          experimentId: string;
          groundTruth?: Json | null;
          id: string;
          input: Json;
          itemDatasetVersion?: number | null;
          itemId: string;
          metadata?: Json | null;
          organizationId?: string | null;
          output?: Json | null;
          projectId?: string | null;
          retryCount: number;
          startedAt: string;
          startedAtZ?: string | null;
          status?: string | null;
          tags?: Json | null;
          toolMockReport?: Json | null;
          traceId?: string | null;
        };
        Update: {
          attempt?: number | null;
          comment?: string | null;
          completedAt?: string;
          completedAtZ?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          error?: Json | null;
          experimentId?: string;
          groundTruth?: Json | null;
          id?: string;
          input?: Json;
          itemDatasetVersion?: number | null;
          itemId?: string;
          metadata?: Json | null;
          organizationId?: string | null;
          output?: Json | null;
          projectId?: string | null;
          retryCount?: number;
          startedAt?: string;
          startedAtZ?: string | null;
          status?: string | null;
          tags?: Json | null;
          toolMockReport?: Json | null;
          traceId?: string | null;
        };
        Relationships: [];
      };
      mastra_experiments: {
        Row: {
          agentVersion: string | null;
          comparisonId: string | null;
          completedAt: string | null;
          completedAtZ: string | null;
          createdAt: string;
          createdAtZ: string | null;
          datasetId: string | null;
          datasetVersion: number | null;
          description: string | null;
          experimentSetId: string | null;
          failedCount: number;
          id: string;
          metadata: Json | null;
          name: string | null;
          organizationId: string | null;
          projectId: string | null;
          provenance: Json | null;
          runnerAttestation: Json | null;
          scorerIds: Json | null;
          skippedCount: number;
          startedAt: string | null;
          startedAtZ: string | null;
          status: string;
          succeededCount: number;
          targetId: string;
          targetType: string;
          totalItems: number;
          trialIndex: number | null;
          updatedAt: string;
          updatedAtZ: string | null;
          variantId: string | null;
        };
        Insert: {
          agentVersion?: string | null;
          comparisonId?: string | null;
          completedAt?: string | null;
          completedAtZ?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          datasetId?: string | null;
          datasetVersion?: number | null;
          description?: string | null;
          experimentSetId?: string | null;
          failedCount: number;
          id: string;
          metadata?: Json | null;
          name?: string | null;
          organizationId?: string | null;
          projectId?: string | null;
          provenance?: Json | null;
          runnerAttestation?: Json | null;
          scorerIds?: Json | null;
          skippedCount: number;
          startedAt?: string | null;
          startedAtZ?: string | null;
          status: string;
          succeededCount: number;
          targetId: string;
          targetType: string;
          totalItems: number;
          trialIndex?: number | null;
          updatedAt: string;
          updatedAtZ?: string | null;
          variantId?: string | null;
        };
        Update: {
          agentVersion?: string | null;
          comparisonId?: string | null;
          completedAt?: string | null;
          completedAtZ?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          datasetId?: string | null;
          datasetVersion?: number | null;
          description?: string | null;
          experimentSetId?: string | null;
          failedCount?: number;
          id?: string;
          metadata?: Json | null;
          name?: string | null;
          organizationId?: string | null;
          projectId?: string | null;
          provenance?: Json | null;
          runnerAttestation?: Json | null;
          scorerIds?: Json | null;
          skippedCount?: number;
          startedAt?: string | null;
          startedAtZ?: string | null;
          status?: string;
          succeededCount?: number;
          targetId?: string;
          targetType?: string;
          totalItems?: number;
          trialIndex?: number | null;
          updatedAt?: string;
          updatedAtZ?: string | null;
          variantId?: string | null;
        };
        Relationships: [];
      };
      mastra_favorites: {
        Row: {
          createdAt: string;
          createdAtZ: string | null;
          entityId: string;
          entityType: string;
          userId: string;
        };
        Insert: {
          createdAt: string;
          createdAtZ?: string | null;
          entityId: string;
          entityType: string;
          userId: string;
        };
        Update: {
          createdAt?: string;
          createdAtZ?: string | null;
          entityId?: string;
          entityType?: string;
          userId?: string;
        };
        Relationships: [];
      };
      mastra_knowledge_activity: {
        Row: {
          action: string;
          createdAt: string;
          createdAtZ: string | null;
          id: string;
          recordId: string;
          recordType: string;
          scope: Json;
          scopeKey: string;
          sourceThreadId: string | null;
        };
        Insert: {
          action: string;
          createdAt: string;
          createdAtZ?: string | null;
          id: string;
          recordId: string;
          recordType: string;
          scope: Json;
          scopeKey: string;
          sourceThreadId?: string | null;
        };
        Update: {
          action?: string;
          createdAt?: string;
          createdAtZ?: string | null;
          id?: string;
          recordId?: string;
          recordType?: string;
          scope?: Json;
          scopeKey?: string;
          sourceThreadId?: string | null;
        };
        Relationships: [];
      };
      mastra_knowledge_cursors: {
        Row: {
          agent: string;
          lastKnowledgeId: string;
          sourceThreadId: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          agent: string;
          lastKnowledgeId: string;
          sourceThreadId: string;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          agent?: string;
          lastKnowledgeId?: string;
          sourceThreadId?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_knowledge_mentions: {
        Row: {
          recordId: string;
          sourceId: string;
          sourceType: string;
        };
        Insert: {
          recordId: string;
          sourceId: string;
          sourceType: string;
        };
        Update: {
          recordId?: string;
          sourceId?: string;
          sourceType?: string;
        };
        Relationships: [];
      };
      mastra_knowledge_nodes: {
        Row: {
          canonicalName: string;
          content: string | null;
          createdAt: string;
          createdAtZ: string | null;
          description: string | null;
          id: string;
          kind: string | null;
          mergedInto: string | null;
          name: string;
          scope: Json;
          scopeKey: string;
          type: string;
          updatedAt: string;
          updatedAtZ: string | null;
          version: number;
        };
        Insert: {
          canonicalName: string;
          content?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          description?: string | null;
          id: string;
          kind?: string | null;
          mergedInto?: string | null;
          name: string;
          scope: Json;
          scopeKey: string;
          type: string;
          updatedAt: string;
          updatedAtZ?: string | null;
          version: number;
        };
        Update: {
          canonicalName?: string;
          content?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          description?: string | null;
          id?: string;
          kind?: string | null;
          mergedInto?: string | null;
          name?: string;
          scope?: Json;
          scopeKey?: string;
          type?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
          version?: number;
        };
        Relationships: [];
      };
      mastra_knowledge_records: {
        Row: {
          capturedAt: string;
          capturedAtZ: string | null;
          deletedAt: string | null;
          deletedAtZ: string | null;
          deletedBy: string | null;
          id: string;
          maxScope: string | null;
          metadata: Json | null;
          node: string;
          scope: Json;
          scopeKey: string;
          sourceThreadId: string;
          text: string;
          when: string | null;
          whenZ: string | null;
        };
        Insert: {
          capturedAt: string;
          capturedAtZ?: string | null;
          deletedAt?: string | null;
          deletedAtZ?: string | null;
          deletedBy?: string | null;
          id: string;
          maxScope?: string | null;
          metadata?: Json | null;
          node: string;
          scope: Json;
          scopeKey: string;
          sourceThreadId: string;
          text: string;
          when?: string | null;
          whenZ?: string | null;
        };
        Update: {
          capturedAt?: string;
          capturedAtZ?: string | null;
          deletedAt?: string | null;
          deletedAtZ?: string | null;
          deletedBy?: string | null;
          id?: string;
          maxScope?: string | null;
          metadata?: Json | null;
          node?: string;
          scope?: Json;
          scopeKey?: string;
          sourceThreadId?: string;
          text?: string;
          when?: string | null;
          whenZ?: string | null;
        };
        Relationships: [];
      };
      mastra_knowledge_semantic_outbox: {
        Row: {
          attempts: number;
          availableAt: string;
          availableAtZ: string | null;
          claimedAt: string | null;
          claimedAtZ: string | null;
          claimedBy: string | null;
          completedAt: string | null;
          completedAtZ: string | null;
          createdAt: string;
          createdAtZ: string | null;
          documentId: string;
          documentType: string;
          id: string;
          idempotencyKey: string;
          operation: string;
          scope: Json;
          scopeKey: string;
          status: string;
        };
        Insert: {
          attempts: number;
          availableAt: string;
          availableAtZ?: string | null;
          claimedAt?: string | null;
          claimedAtZ?: string | null;
          claimedBy?: string | null;
          completedAt?: string | null;
          completedAtZ?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          documentId: string;
          documentType: string;
          id: string;
          idempotencyKey: string;
          operation: string;
          scope: Json;
          scopeKey: string;
          status: string;
        };
        Update: {
          attempts?: number;
          availableAt?: string;
          availableAtZ?: string | null;
          claimedAt?: string | null;
          claimedAtZ?: string | null;
          claimedBy?: string | null;
          completedAt?: string | null;
          completedAtZ?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          documentId?: string;
          documentType?: string;
          id?: string;
          idempotencyKey?: string;
          operation?: string;
          scope?: Json;
          scopeKey?: string;
          status?: string;
        };
        Relationships: [];
      };
      mastra_mcp_client_versions: {
        Row: {
          changedFields: Json | null;
          changeMessage: string | null;
          createdAt: string;
          createdAtZ: string | null;
          description: string | null;
          id: string;
          mcpClientId: string;
          name: string;
          servers: Json;
          versionNumber: number;
        };
        Insert: {
          changedFields?: Json | null;
          changeMessage?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          description?: string | null;
          id: string;
          mcpClientId: string;
          name: string;
          servers: Json;
          versionNumber: number;
        };
        Update: {
          changedFields?: Json | null;
          changeMessage?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          description?: string | null;
          id?: string;
          mcpClientId?: string;
          name?: string;
          servers?: Json;
          versionNumber?: number;
        };
        Relationships: [];
      };
      mastra_mcp_clients: {
        Row: {
          activeVersionId: string | null;
          authorId: string | null;
          createdAt: string;
          createdAtZ: string | null;
          id: string;
          metadata: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          id: string;
          metadata?: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          id?: string;
          metadata?: Json | null;
          status?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_mcp_server_versions: {
        Row: {
          agents: Json | null;
          changedFields: Json | null;
          changeMessage: string | null;
          createdAt: string;
          createdAtZ: string | null;
          description: string | null;
          id: string;
          instructions: string | null;
          isLatest: boolean | null;
          mcpServerId: string;
          name: string;
          packageCanonical: string | null;
          releaseDate: string | null;
          repository: Json | null;
          tools: Json | null;
          version: string;
          versionNumber: number;
          workflows: Json | null;
        };
        Insert: {
          agents?: Json | null;
          changedFields?: Json | null;
          changeMessage?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          description?: string | null;
          id: string;
          instructions?: string | null;
          isLatest?: boolean | null;
          mcpServerId: string;
          name: string;
          packageCanonical?: string | null;
          releaseDate?: string | null;
          repository?: Json | null;
          tools?: Json | null;
          version: string;
          versionNumber: number;
          workflows?: Json | null;
        };
        Update: {
          agents?: Json | null;
          changedFields?: Json | null;
          changeMessage?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          description?: string | null;
          id?: string;
          instructions?: string | null;
          isLatest?: boolean | null;
          mcpServerId?: string;
          name?: string;
          packageCanonical?: string | null;
          releaseDate?: string | null;
          repository?: Json | null;
          tools?: Json | null;
          version?: string;
          versionNumber?: number;
          workflows?: Json | null;
        };
        Relationships: [];
      };
      mastra_mcp_servers: {
        Row: {
          activeVersionId: string | null;
          authorId: string | null;
          createdAt: string;
          createdAtZ: string | null;
          id: string;
          metadata: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          id: string;
          metadata?: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          id?: string;
          metadata?: Json | null;
          status?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_messages: {
        Row: {
          content: string;
          createdAt: string;
          createdAtZ: string | null;
          id: string;
          resourceId: string | null;
          role: string;
          thread_id: string;
          type: string;
        };
        Insert: {
          content: string;
          createdAt?: string;
          createdAtZ?: string | null;
          id: string;
          resourceId?: string | null;
          role: string;
          thread_id: string;
          type: string;
        };
        Update: {
          content?: string;
          createdAt?: string;
          createdAtZ?: string | null;
          id?: string;
          resourceId?: string | null;
          role?: string;
          thread_id?: string;
          type?: string;
        };
        Relationships: [];
      };
      mastra_migrations: {
        Row: {
          created_at: number | null;
          hash: string;
          id: number;
        };
        Insert: {
          created_at?: number | null;
          hash: string;
          id?: number;
        };
        Update: {
          created_at?: number | null;
          hash?: string;
          id?: number;
        };
        Relationships: [];
      };
      mastra_notifications: {
        Row: {
          agentId: string | null;
          archivedAt: string | null;
          archivedAtZ: string | null;
          attributes: Json | null;
          coalescedCount: number;
          coalesceKey: string | null;
          createdAt: string;
          createdAtZ: string | null;
          dedupeKey: string | null;
          deliverAt: string | null;
          deliverAtZ: string | null;
          deliveredAt: string | null;
          deliveredAtZ: string | null;
          deliveredSignalId: string | null;
          deliveryAttempts: number;
          deliveryReason: string | null;
          discardedAt: string | null;
          discardedAtZ: string | null;
          dismissedAt: string | null;
          dismissedAtZ: string | null;
          id: string;
          kind: string;
          lastDeliveryAttemptAt: string | null;
          lastDeliveryAttemptAtZ: string | null;
          lastDeliveryError: string | null;
          metadata: Json | null;
          payload: Json | null;
          priority: string;
          resourceId: string | null;
          seenAt: string | null;
          seenAtZ: string | null;
          source: string;
          sourceId: string | null;
          status: string;
          summary: string;
          summaryAt: string | null;
          summaryAtZ: string | null;
          summarySignalId: string | null;
          threadId: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          agentId?: string | null;
          archivedAt?: string | null;
          archivedAtZ?: string | null;
          attributes?: Json | null;
          coalescedCount: number;
          coalesceKey?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          dedupeKey?: string | null;
          deliverAt?: string | null;
          deliverAtZ?: string | null;
          deliveredAt?: string | null;
          deliveredAtZ?: string | null;
          deliveredSignalId?: string | null;
          deliveryAttempts: number;
          deliveryReason?: string | null;
          discardedAt?: string | null;
          discardedAtZ?: string | null;
          dismissedAt?: string | null;
          dismissedAtZ?: string | null;
          id: string;
          kind: string;
          lastDeliveryAttemptAt?: string | null;
          lastDeliveryAttemptAtZ?: string | null;
          lastDeliveryError?: string | null;
          metadata?: Json | null;
          payload?: Json | null;
          priority: string;
          resourceId?: string | null;
          seenAt?: string | null;
          seenAtZ?: string | null;
          source: string;
          sourceId?: string | null;
          status: string;
          summary: string;
          summaryAt?: string | null;
          summaryAtZ?: string | null;
          summarySignalId?: string | null;
          threadId: string;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          agentId?: string | null;
          archivedAt?: string | null;
          archivedAtZ?: string | null;
          attributes?: Json | null;
          coalescedCount?: number;
          coalesceKey?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          dedupeKey?: string | null;
          deliverAt?: string | null;
          deliverAtZ?: string | null;
          deliveredAt?: string | null;
          deliveredAtZ?: string | null;
          deliveredSignalId?: string | null;
          deliveryAttempts?: number;
          deliveryReason?: string | null;
          discardedAt?: string | null;
          discardedAtZ?: string | null;
          dismissedAt?: string | null;
          dismissedAtZ?: string | null;
          id?: string;
          kind?: string;
          lastDeliveryAttemptAt?: string | null;
          lastDeliveryAttemptAtZ?: string | null;
          lastDeliveryError?: string | null;
          metadata?: Json | null;
          payload?: Json | null;
          priority?: string;
          resourceId?: string | null;
          seenAt?: string | null;
          seenAtZ?: string | null;
          source?: string;
          sourceId?: string | null;
          status?: string;
          summary?: string;
          summaryAt?: string | null;
          summaryAtZ?: string | null;
          summarySignalId?: string | null;
          threadId?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_observational_memory: {
        Row: {
          activeObservations: string;
          activeObservationsPendingUpdate: string | null;
          bufferedMessageIds: Json | null;
          bufferedObservationChunks: Json | null;
          bufferedObservations: string | null;
          bufferedObservationTokens: number | null;
          bufferedReflection: string | null;
          bufferedReflectionInputTokens: number | null;
          bufferedReflectionTokens: number | null;
          config: string;
          createdAt: string;
          createdAtZ: string | null;
          generationCount: number;
          id: string;
          isBufferingObservation: boolean;
          isBufferingReflection: boolean;
          isObserving: boolean;
          isReflecting: boolean;
          lastBufferedAtTime: string | null;
          lastBufferedAtTimeZ: string | null;
          lastBufferedAtTokens: number;
          lastObservedAt: string | null;
          lastObservedAtZ: string | null;
          lastReflectionAt: string | null;
          lastReflectionAtZ: string | null;
          lookupKey: string;
          metadata: Json | null;
          observationTokenCount: number;
          observedMessageIds: Json | null;
          observedTimezone: string | null;
          originType: string;
          pendingMessageTokens: number;
          reflectedObservationLineCount: number | null;
          resourceId: string | null;
          scope: string;
          threadId: string | null;
          totalTokensObserved: number;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          activeObservations: string;
          activeObservationsPendingUpdate?: string | null;
          bufferedMessageIds?: Json | null;
          bufferedObservationChunks?: Json | null;
          bufferedObservations?: string | null;
          bufferedObservationTokens?: number | null;
          bufferedReflection?: string | null;
          bufferedReflectionInputTokens?: number | null;
          bufferedReflectionTokens?: number | null;
          config: string;
          createdAt: string;
          createdAtZ?: string | null;
          generationCount: number;
          id: string;
          isBufferingObservation: boolean;
          isBufferingReflection: boolean;
          isObserving: boolean;
          isReflecting: boolean;
          lastBufferedAtTime?: string | null;
          lastBufferedAtTimeZ?: string | null;
          lastBufferedAtTokens: number;
          lastObservedAt?: string | null;
          lastObservedAtZ?: string | null;
          lastReflectionAt?: string | null;
          lastReflectionAtZ?: string | null;
          lookupKey: string;
          metadata?: Json | null;
          observationTokenCount: number;
          observedMessageIds?: Json | null;
          observedTimezone?: string | null;
          originType: string;
          pendingMessageTokens: number;
          reflectedObservationLineCount?: number | null;
          resourceId?: string | null;
          scope: string;
          threadId?: string | null;
          totalTokensObserved: number;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          activeObservations?: string;
          activeObservationsPendingUpdate?: string | null;
          bufferedMessageIds?: Json | null;
          bufferedObservationChunks?: Json | null;
          bufferedObservations?: string | null;
          bufferedObservationTokens?: number | null;
          bufferedReflection?: string | null;
          bufferedReflectionInputTokens?: number | null;
          bufferedReflectionTokens?: number | null;
          config?: string;
          createdAt?: string;
          createdAtZ?: string | null;
          generationCount?: number;
          id?: string;
          isBufferingObservation?: boolean;
          isBufferingReflection?: boolean;
          isObserving?: boolean;
          isReflecting?: boolean;
          lastBufferedAtTime?: string | null;
          lastBufferedAtTimeZ?: string | null;
          lastBufferedAtTokens?: number;
          lastObservedAt?: string | null;
          lastObservedAtZ?: string | null;
          lastReflectionAt?: string | null;
          lastReflectionAtZ?: string | null;
          lookupKey?: string;
          metadata?: Json | null;
          observationTokenCount?: number;
          observedMessageIds?: Json | null;
          observedTimezone?: string | null;
          originType?: string;
          pendingMessageTokens?: number;
          reflectedObservationLineCount?: number | null;
          resourceId?: string | null;
          scope?: string;
          threadId?: string | null;
          totalTokensObserved?: number;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_prompt_block_versions: {
        Row: {
          blockId: string;
          changedFields: Json | null;
          changeMessage: string | null;
          content: string;
          createdAt: string;
          createdAtZ: string | null;
          description: string | null;
          id: string;
          name: string;
          requestContextSchema: Json | null;
          rules: Json | null;
          versionNumber: number;
        };
        Insert: {
          blockId: string;
          changedFields?: Json | null;
          changeMessage?: string | null;
          content: string;
          createdAt: string;
          createdAtZ?: string | null;
          description?: string | null;
          id: string;
          name: string;
          requestContextSchema?: Json | null;
          rules?: Json | null;
          versionNumber: number;
        };
        Update: {
          blockId?: string;
          changedFields?: Json | null;
          changeMessage?: string | null;
          content?: string;
          createdAt?: string;
          createdAtZ?: string | null;
          description?: string | null;
          id?: string;
          name?: string;
          requestContextSchema?: Json | null;
          rules?: Json | null;
          versionNumber?: number;
        };
        Relationships: [];
      };
      mastra_prompt_blocks: {
        Row: {
          activeVersionId: string | null;
          authorId: string | null;
          createdAt: string;
          createdAtZ: string | null;
          id: string;
          metadata: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          id: string;
          metadata?: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          id?: string;
          metadata?: Json | null;
          status?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_resources: {
        Row: {
          createdAt: string;
          createdAtZ: string | null;
          id: string;
          metadata: Json | null;
          updatedAt: string;
          updatedAtZ: string | null;
          workingMemory: string | null;
        };
        Insert: {
          createdAt?: string;
          createdAtZ?: string | null;
          id: string;
          metadata?: Json | null;
          updatedAt?: string;
          updatedAtZ?: string | null;
          workingMemory?: string | null;
        };
        Update: {
          createdAt?: string;
          createdAtZ?: string | null;
          id?: string;
          metadata?: Json | null;
          updatedAt?: string;
          updatedAtZ?: string | null;
          workingMemory?: string | null;
        };
        Relationships: [];
      };
      mastra_schedule_triggers: {
        Row: {
          actual_fire_at: number;
          error: string | null;
          id: string;
          metadata: Json | null;
          outcome: string;
          parent_trigger_id: string | null;
          run_id: string | null;
          schedule_id: string;
          scheduled_fire_at: number;
          trigger_kind: string;
        };
        Insert: {
          actual_fire_at: number;
          error?: string | null;
          id: string;
          metadata?: Json | null;
          outcome: string;
          parent_trigger_id?: string | null;
          run_id?: string | null;
          schedule_id: string;
          scheduled_fire_at: number;
          trigger_kind: string;
        };
        Update: {
          actual_fire_at?: number;
          error?: string | null;
          id?: string;
          metadata?: Json | null;
          outcome?: string;
          parent_trigger_id?: string | null;
          run_id?: string | null;
          schedule_id?: string;
          scheduled_fire_at?: number;
          trigger_kind?: string;
        };
        Relationships: [];
      };
      mastra_schedules: {
        Row: {
          created_at: number;
          cron: string;
          id: string;
          last_fire_at: number | null;
          last_run_id: string | null;
          metadata: Json | null;
          next_fire_at: number;
          owner_id: string | null;
          owner_type: string | null;
          status: string;
          target: Json;
          timezone: string | null;
          updated_at: number;
        };
        Insert: {
          created_at: number;
          cron: string;
          id: string;
          last_fire_at?: number | null;
          last_run_id?: string | null;
          metadata?: Json | null;
          next_fire_at: number;
          owner_id?: string | null;
          owner_type?: string | null;
          status: string;
          target: Json;
          timezone?: string | null;
          updated_at: number;
        };
        Update: {
          created_at?: number;
          cron?: string;
          id?: string;
          last_fire_at?: number | null;
          last_run_id?: string | null;
          metadata?: Json | null;
          next_fire_at?: number;
          owner_id?: string | null;
          owner_type?: string | null;
          status?: string;
          target?: Json;
          timezone?: string | null;
          updated_at?: number;
        };
        Relationships: [];
      };
      mastra_scorer_definition_versions: {
        Row: {
          changedFields: Json | null;
          changeMessage: string | null;
          createdAt: string;
          createdAtZ: string | null;
          defaultSampling: Json | null;
          description: string | null;
          id: string;
          instructions: string | null;
          model: Json | null;
          name: string;
          presetConfig: Json | null;
          scoreRange: Json | null;
          scorerDefinitionId: string;
          type: string;
          versionNumber: number;
        };
        Insert: {
          changedFields?: Json | null;
          changeMessage?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          defaultSampling?: Json | null;
          description?: string | null;
          id: string;
          instructions?: string | null;
          model?: Json | null;
          name: string;
          presetConfig?: Json | null;
          scoreRange?: Json | null;
          scorerDefinitionId: string;
          type: string;
          versionNumber: number;
        };
        Update: {
          changedFields?: Json | null;
          changeMessage?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          defaultSampling?: Json | null;
          description?: string | null;
          id?: string;
          instructions?: string | null;
          model?: Json | null;
          name?: string;
          presetConfig?: Json | null;
          scoreRange?: Json | null;
          scorerDefinitionId?: string;
          type?: string;
          versionNumber?: number;
        };
        Relationships: [];
      };
      mastra_scorer_definitions: {
        Row: {
          activeVersionId: string | null;
          authorId: string | null;
          createdAt: string;
          createdAtZ: string | null;
          id: string;
          metadata: Json | null;
          organizationId: string | null;
          projectId: string | null;
          status: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          id: string;
          metadata?: Json | null;
          organizationId?: string | null;
          projectId?: string | null;
          status: string;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          id?: string;
          metadata?: Json | null;
          organizationId?: string | null;
          projectId?: string | null;
          status?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_scorers: {
        Row: {
          additionalContext: Json | null;
          analyzePrompt: string | null;
          analyzeStepResult: Json | null;
          batchId: string | null;
          createdAt: string | null;
          createdAtZ: string | null;
          datasetId: string | null;
          datasetItemId: string | null;
          entity: Json | null;
          entityId: string | null;
          entityType: string | null;
          extractPrompt: string | null;
          extractStepResult: Json | null;
          generateReasonPrompt: string | null;
          generateScorePrompt: string | null;
          id: string;
          input: Json;
          metadata: Json | null;
          organizationId: string | null;
          output: Json;
          preprocessPrompt: string | null;
          preprocessStepResult: Json | null;
          projectId: string | null;
          reason: string | null;
          reasonPrompt: string | null;
          requestContext: Json | null;
          resourceId: string | null;
          runId: string;
          runtimeContext: Json | null;
          score: number;
          scorer: Json;
          scorerId: string;
          source: string;
          spanId: string | null;
          threadId: string | null;
          traceId: string | null;
          updatedAt: string | null;
          updatedAtZ: string | null;
        };
        Insert: {
          additionalContext?: Json | null;
          analyzePrompt?: string | null;
          analyzeStepResult?: Json | null;
          batchId?: string | null;
          createdAt?: string | null;
          createdAtZ?: string | null;
          datasetId?: string | null;
          datasetItemId?: string | null;
          entity?: Json | null;
          entityId?: string | null;
          entityType?: string | null;
          extractPrompt?: string | null;
          extractStepResult?: Json | null;
          generateReasonPrompt?: string | null;
          generateScorePrompt?: string | null;
          id: string;
          input: Json;
          metadata?: Json | null;
          organizationId?: string | null;
          output: Json;
          preprocessPrompt?: string | null;
          preprocessStepResult?: Json | null;
          projectId?: string | null;
          reason?: string | null;
          reasonPrompt?: string | null;
          requestContext?: Json | null;
          resourceId?: string | null;
          runId: string;
          runtimeContext?: Json | null;
          score: number;
          scorer: Json;
          scorerId: string;
          source: string;
          spanId?: string | null;
          threadId?: string | null;
          traceId?: string | null;
          updatedAt?: string | null;
          updatedAtZ?: string | null;
        };
        Update: {
          additionalContext?: Json | null;
          analyzePrompt?: string | null;
          analyzeStepResult?: Json | null;
          batchId?: string | null;
          createdAt?: string | null;
          createdAtZ?: string | null;
          datasetId?: string | null;
          datasetItemId?: string | null;
          entity?: Json | null;
          entityId?: string | null;
          entityType?: string | null;
          extractPrompt?: string | null;
          extractStepResult?: Json | null;
          generateReasonPrompt?: string | null;
          generateScorePrompt?: string | null;
          id?: string;
          input?: Json;
          metadata?: Json | null;
          organizationId?: string | null;
          output?: Json;
          preprocessPrompt?: string | null;
          preprocessStepResult?: Json | null;
          projectId?: string | null;
          reason?: string | null;
          reasonPrompt?: string | null;
          requestContext?: Json | null;
          resourceId?: string | null;
          runId?: string;
          runtimeContext?: Json | null;
          score?: number;
          scorer?: Json;
          scorerId?: string;
          source?: string;
          spanId?: string | null;
          threadId?: string | null;
          traceId?: string | null;
          updatedAt?: string | null;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_skill_blobs: {
        Row: {
          content: string;
          createdAt: string;
          createdAtZ: string | null;
          hash: string;
          mimeType: string | null;
          size: number;
        };
        Insert: {
          content: string;
          createdAt: string;
          createdAtZ?: string | null;
          hash: string;
          mimeType?: string | null;
          size: number;
        };
        Update: {
          content?: string;
          createdAt?: string;
          createdAtZ?: string | null;
          hash?: string;
          mimeType?: string | null;
          size?: number;
        };
        Relationships: [];
      };
      mastra_skill_versions: {
        Row: {
          assets: Json | null;
          changedFields: Json | null;
          changeMessage: string | null;
          compatibility: Json | null;
          createdAt: string;
          createdAtZ: string | null;
          description: string;
          files: Json | null;
          id: string;
          instructions: string;
          license: string | null;
          metadata: Json | null;
          name: string;
          references: Json | null;
          scripts: Json | null;
          skillId: string;
          source: Json | null;
          tree: Json | null;
          versionNumber: number;
        };
        Insert: {
          assets?: Json | null;
          changedFields?: Json | null;
          changeMessage?: string | null;
          compatibility?: Json | null;
          createdAt: string;
          createdAtZ?: string | null;
          description: string;
          files?: Json | null;
          id: string;
          instructions: string;
          license?: string | null;
          metadata?: Json | null;
          name: string;
          references?: Json | null;
          scripts?: Json | null;
          skillId: string;
          source?: Json | null;
          tree?: Json | null;
          versionNumber: number;
        };
        Update: {
          assets?: Json | null;
          changedFields?: Json | null;
          changeMessage?: string | null;
          compatibility?: Json | null;
          createdAt?: string;
          createdAtZ?: string | null;
          description?: string;
          files?: Json | null;
          id?: string;
          instructions?: string;
          license?: string | null;
          metadata?: Json | null;
          name?: string;
          references?: Json | null;
          scripts?: Json | null;
          skillId?: string;
          source?: Json | null;
          tree?: Json | null;
          versionNumber?: number;
        };
        Relationships: [];
      };
      mastra_skills: {
        Row: {
          activeVersionId: string | null;
          authorId: string | null;
          createdAt: string;
          createdAtZ: string | null;
          favoriteCount: number | null;
          id: string;
          status: string;
          updatedAt: string;
          updatedAtZ: string | null;
          visibility: string | null;
        };
        Insert: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          favoriteCount?: number | null;
          id: string;
          status: string;
          updatedAt: string;
          updatedAtZ?: string | null;
          visibility?: string | null;
        };
        Update: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          favoriteCount?: number | null;
          id?: string;
          status?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
          visibility?: string | null;
        };
        Relationships: [];
      };
      mastra_thread_state: {
        Row: {
          createdAt: string;
          createdAtZ: string | null;
          threadId: string;
          type: string;
          updatedAt: string;
          updatedAtZ: string | null;
          value: Json;
        };
        Insert: {
          createdAt: string;
          createdAtZ?: string | null;
          threadId: string;
          type: string;
          updatedAt: string;
          updatedAtZ?: string | null;
          value: Json;
        };
        Update: {
          createdAt?: string;
          createdAtZ?: string | null;
          threadId?: string;
          type?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
          value?: Json;
        };
        Relationships: [];
      };
      mastra_threads: {
        Row: {
          createdAt: string;
          createdAtZ: string | null;
          id: string;
          metadata: string | null;
          resourceId: string;
          title: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          createdAt?: string;
          createdAtZ?: string | null;
          id: string;
          metadata?: string | null;
          resourceId: string;
          title: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Update: {
          createdAt?: string;
          createdAtZ?: string | null;
          id?: string;
          metadata?: string | null;
          resourceId?: string;
          title?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_tool_provider_connections: {
        Row: {
          authorId: string;
          connectionId: string;
          createdAt: string;
          createdAtZ: string | null;
          label: string | null;
          providerId: string;
          scope: string;
          toolkit: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          authorId: string;
          connectionId: string;
          createdAt: string;
          createdAtZ?: string | null;
          label?: string | null;
          providerId: string;
          scope: string;
          toolkit: string;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          authorId?: string;
          connectionId?: string;
          createdAt?: string;
          createdAtZ?: string | null;
          label?: string | null;
          providerId?: string;
          scope?: string;
          toolkit?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_traces: {
        Row: {
          attributes: Json | null;
          createdAt: string;
          createdAtZ: string | null;
          endTime: number;
          events: Json | null;
          id: string;
          kind: number;
          links: Json | null;
          name: string;
          other: string | null;
          parentSpanId: string | null;
          scope: string;
          startTime: number;
          status: Json | null;
          traceId: string;
        };
        Insert: {
          attributes?: Json | null;
          createdAt?: string;
          createdAtZ?: string | null;
          endTime: number;
          events?: Json | null;
          id: string;
          kind: number;
          links?: Json | null;
          name: string;
          other?: string | null;
          parentSpanId?: string | null;
          scope: string;
          startTime: number;
          status?: Json | null;
          traceId: string;
        };
        Update: {
          attributes?: Json | null;
          createdAt?: string;
          createdAtZ?: string | null;
          endTime?: number;
          events?: Json | null;
          id?: string;
          kind?: number;
          links?: Json | null;
          name?: string;
          other?: string | null;
          parentSpanId?: string | null;
          scope?: string;
          startTime?: number;
          status?: Json | null;
          traceId?: string;
        };
        Relationships: [];
      };
      mastra_workflow_definitions: {
        Row: {
          authorId: string | null;
          createdAt: string;
          createdAtZ: string | null;
          description: string | null;
          graph: Json;
          id: string;
          inputSchema: Json;
          metadata: Json | null;
          outputSchema: Json;
          requestContextSchema: Json | null;
          schedule: Json | null;
          source: string;
          stateSchema: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          authorId?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          description?: string | null;
          graph: Json;
          id: string;
          inputSchema: Json;
          metadata?: Json | null;
          outputSchema: Json;
          requestContextSchema?: Json | null;
          schedule?: Json | null;
          source: string;
          stateSchema?: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          authorId?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          description?: string | null;
          graph?: Json;
          id?: string;
          inputSchema?: Json;
          metadata?: Json | null;
          outputSchema?: Json;
          requestContextSchema?: Json | null;
          schedule?: Json | null;
          source?: string;
          stateSchema?: Json | null;
          status?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      mastra_workflow_snapshot: {
        Row: {
          createdAt: string | null;
          createdAtZ: string | null;
          resourceId: string | null;
          run_id: string;
          snapshot: string;
          updatedAt: string | null;
          updatedAtZ: string | null;
          workflow_name: string;
        };
        Insert: {
          createdAt?: string | null;
          createdAtZ?: string | null;
          resourceId?: string | null;
          run_id: string;
          snapshot: string;
          updatedAt?: string | null;
          updatedAtZ?: string | null;
          workflow_name: string;
        };
        Update: {
          createdAt?: string | null;
          createdAtZ?: string | null;
          resourceId?: string | null;
          run_id?: string;
          snapshot?: string;
          updatedAt?: string | null;
          updatedAtZ?: string | null;
          workflow_name?: string;
        };
        Relationships: [];
      };
      mastra_workspace_versions: {
        Row: {
          autoSync: boolean | null;
          changedFields: Json | null;
          changeMessage: string | null;
          createdAt: string;
          createdAtZ: string | null;
          description: string | null;
          filesystem: Json | null;
          id: string;
          mounts: Json | null;
          name: string;
          operationTimeout: number | null;
          sandbox: Json | null;
          search: Json | null;
          skills: Json | null;
          tools: Json | null;
          versionNumber: number;
          workspaceId: string;
        };
        Insert: {
          autoSync?: boolean | null;
          changedFields?: Json | null;
          changeMessage?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          description?: string | null;
          filesystem?: Json | null;
          id: string;
          mounts?: Json | null;
          name: string;
          operationTimeout?: number | null;
          sandbox?: Json | null;
          search?: Json | null;
          skills?: Json | null;
          tools?: Json | null;
          versionNumber: number;
          workspaceId: string;
        };
        Update: {
          autoSync?: boolean | null;
          changedFields?: Json | null;
          changeMessage?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          description?: string | null;
          filesystem?: Json | null;
          id?: string;
          mounts?: Json | null;
          name?: string;
          operationTimeout?: number | null;
          sandbox?: Json | null;
          search?: Json | null;
          skills?: Json | null;
          tools?: Json | null;
          versionNumber?: number;
          workspaceId?: string;
        };
        Relationships: [];
      };
      mastra_workspaces: {
        Row: {
          activeVersionId: string | null;
          authorId: string | null;
          createdAt: string;
          createdAtZ: string | null;
          id: string;
          metadata: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ: string | null;
        };
        Insert: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt: string;
          createdAtZ?: string | null;
          id: string;
          metadata?: Json | null;
          status: string;
          updatedAt: string;
          updatedAtZ?: string | null;
        };
        Update: {
          activeVersionId?: string | null;
          authorId?: string | null;
          createdAt?: string;
          createdAtZ?: string | null;
          id?: string;
          metadata?: Json | null;
          status?: string;
          updatedAt?: string;
          updatedAtZ?: string | null;
        };
        Relationships: [];
      };
      medical_text_examples: {
        Row: {
          checked_by_coder: boolean;
          created_at: string | null;
          id: string;
          is_follow_up: boolean;
          label: string | null;
          notes: string | null;
          sequence_number: number;
          test_type: string | null;
          text: string | null;
          updated_at: string | null;
        };
        Insert: {
          checked_by_coder?: boolean;
          created_at?: string | null;
          id?: string;
          is_follow_up?: boolean;
          label?: string | null;
          notes?: string | null;
          sequence_number?: number;
          test_type?: string | null;
          text?: string | null;
          updated_at?: string | null;
        };
        Update: {
          checked_by_coder?: boolean;
          created_at?: string | null;
          id?: string;
          is_follow_up?: boolean;
          label?: string | null;
          notes?: string | null;
          sequence_number?: number;
          test_type?: string | null;
          text?: string | null;
          updated_at?: string | null;
        };
        Relationships: [];
      };
      medical_text_issue_comments: {
        Row: {
          comment_text: string;
          created_at: string;
          created_by: string;
          id: string;
          issue_id: string;
          updated_at: string;
        };
        Insert: {
          comment_text: string;
          created_at?: string;
          created_by: string;
          id?: string;
          issue_id: string;
          updated_at?: string;
        };
        Update: {
          comment_text?: string;
          created_at?: string;
          created_by?: string;
          id?: string;
          issue_id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "medical_text_issue_comments_created_by_fkey";
            columns: ["created_by"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "medical_text_issue_comments_created_by_fkey";
            columns: ["created_by"];
            isOneToOne: false;
            referencedRelation: "v_profiles_for_analytics";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "medical_text_issue_comments_issue_id_fkey";
            columns: ["issue_id"];
            isOneToOne: false;
            referencedRelation: "medical_text_issues";
            referencedColumns: ["id"];
          },
        ];
      };
      medical_text_issues: {
        Row: {
          created_at: string;
          created_by: string;
          description: string | null;
          id: string;
          medical_text_example_id: string;
          resolved_at: string | null;
          resolved_by: string | null;
          status: Database["public"]["Enums"]["issue_status"];
          title: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          created_by: string;
          description?: string | null;
          id?: string;
          medical_text_example_id: string;
          resolved_at?: string | null;
          resolved_by?: string | null;
          status?: Database["public"]["Enums"]["issue_status"];
          title: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          created_by?: string;
          description?: string | null;
          id?: string;
          medical_text_example_id?: string;
          resolved_at?: string | null;
          resolved_by?: string | null;
          status?: Database["public"]["Enums"]["issue_status"];
          title?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "medical_text_issues_created_by_fkey";
            columns: ["created_by"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "medical_text_issues_created_by_fkey";
            columns: ["created_by"];
            isOneToOne: false;
            referencedRelation: "v_profiles_for_analytics";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "medical_text_issues_medical_text_example_id_fkey";
            columns: ["medical_text_example_id"];
            isOneToOne: false;
            referencedRelation: "medical_text_examples";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "medical_text_issues_resolved_by_fkey";
            columns: ["resolved_by"];
            isOneToOne: false;
            referencedRelation: "profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "medical_text_issues_resolved_by_fkey";
            columns: ["resolved_by"];
            isOneToOne: false;
            referencedRelation: "v_profiles_for_analytics";
            referencedColumns: ["id"];
          },
        ];
      };
      medisight_document_data: {
        Row: {
          created_at: string;
          document_id: string;
          observation_datetime: string;
          observation_identifier: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          document_id: string;
          observation_datetime: string;
          observation_identifier: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          document_id?: string;
          observation_datetime?: string;
          observation_identifier?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "medisight_document_data_document_id_fkey";
            columns: ["document_id"];
            isOneToOne: true;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
        ];
      };
      mediviewer_bundles: {
        Row: {
          created_at: string;
          edms_bundle_id: string;
          edms_bundle_revision: number;
          encounter_id: string;
          fetched_at: string | null;
          id: string;
          outcome: Database["public"]["Enums"]["mediviewer_bundle_outcome"] | null;
          patient_id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          edms_bundle_id: string;
          edms_bundle_revision: number;
          encounter_id: string;
          fetched_at?: string | null;
          id?: string;
          outcome?: Database["public"]["Enums"]["mediviewer_bundle_outcome"] | null;
          patient_id: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          edms_bundle_id?: string;
          edms_bundle_revision?: number;
          encounter_id?: string;
          fetched_at?: string | null;
          id?: string;
          outcome?: Database["public"]["Enums"]["mediviewer_bundle_outcome"] | null;
          patient_id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_encounter_id_patient_encounters_id_fk";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "mediviewer_bundles_patient_id_patients_id_fk";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      opcs_4_code_rule_assignments: {
        Row: {
          code_id: string;
          created_at: string;
          id: string;
          rule_id: string;
        };
        Insert: {
          code_id: string;
          created_at?: string;
          id?: string;
          rule_id: string;
        };
        Update: {
          code_id?: string;
          created_at?: string;
          id?: string;
          rule_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "opcs_4_code_rule_assignments_code_id_fkey";
            columns: ["code_id"];
            isOneToOne: false;
            referencedRelation: "opcs4_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "opcs_4_code_rule_assignments_rule_id_fkey";
            columns: ["rule_id"];
            isOneToOne: false;
            referencedRelation: "opcs_4_code_rules";
            referencedColumns: ["id"];
          },
        ];
      };
      opcs_4_code_rules: {
        Row: {
          created_at: string;
          id: string;
          is_active: boolean;
          name: string;
          rule_text: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          is_active?: boolean;
          name: string;
          rule_text: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          is_active?: boolean;
          name?: string;
          rule_text?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      opcs4_codes: {
        Row: {
          code: string;
          created_at: string;
          description: string;
          id: string;
          last_updated_at: string | null;
          last_updated_description: string | null;
          updated_at: string;
          version: number;
        };
        Insert: {
          code: string;
          created_at?: string;
          description: string;
          id?: string;
          last_updated_at?: string | null;
          last_updated_description?: string | null;
          updated_at?: string;
          version: number;
        };
        Update: {
          code?: string;
          created_at?: string;
          description?: string;
          id?: string;
          last_updated_at?: string | null;
          last_updated_description?: string | null;
          updated_at?: string;
          version?: number;
        };
        Relationships: [];
      };
      outbound_coding_statuses: {
        Row: {
          acknowledgement_code: string | null;
          built_from_icd10_codes: string[] | null;
          built_from_opcs4_codes: string[] | null;
          claim_token: string;
          claimed_at: string;
          coding_status: Database["public"]["Enums"]["outbound_coding_status"];
          created_at: string;
          customer_id: string;
          delivery: Database["public"]["Enums"]["outbound_coding_status_delivery"];
          encounter_id: string;
          error_message: string | null;
          hrg: string | null;
          id: string;
          message_control_id: string | null;
          outbound_write_back_id: string | null;
          sent_at: string | null;
        };
        Insert: {
          acknowledgement_code?: string | null;
          built_from_icd10_codes?: string[] | null;
          built_from_opcs4_codes?: string[] | null;
          claim_token?: string;
          claimed_at?: string;
          coding_status: Database["public"]["Enums"]["outbound_coding_status"];
          created_at?: string;
          customer_id: string;
          delivery?: Database["public"]["Enums"]["outbound_coding_status_delivery"];
          encounter_id: string;
          error_message?: string | null;
          hrg?: string | null;
          id?: string;
          message_control_id?: string | null;
          outbound_write_back_id?: string | null;
          sent_at?: string | null;
        };
        Update: {
          acknowledgement_code?: string | null;
          built_from_icd10_codes?: string[] | null;
          built_from_opcs4_codes?: string[] | null;
          claim_token?: string;
          claimed_at?: string;
          coding_status?: Database["public"]["Enums"]["outbound_coding_status"];
          created_at?: string;
          customer_id?: string;
          delivery?: Database["public"]["Enums"]["outbound_coding_status_delivery"];
          encounter_id?: string;
          error_message?: string | null;
          hrg?: string | null;
          id?: string;
          message_control_id?: string | null;
          outbound_write_back_id?: string | null;
          sent_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "outbound_coding_statuses_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_outbound_write_back_id_fkey";
            columns: ["outbound_write_back_id"];
            isOneToOne: false;
            referencedRelation: "outbound_result_write_backs";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_coding_statuses_outbound_write_back_id_fkey";
            columns: ["outbound_write_back_id"];
            isOneToOne: false;
            referencedRelation: "v_write_backs_for_analytics";
            referencedColumns: ["id"];
          },
        ];
      };
      outbound_result_write_backs: {
        Row: {
          auto_writeback_rule_id: string | null;
          built_from_icd10_codes: string[] | null;
          built_from_opcs4_codes: string[] | null;
          created_at: string;
          customer_id: string;
          encounter_id: string;
          error_message: string | null;
          human_review_id: string | null;
          id: string;
          inbound_classification_result_id: string | null;
          payload_icd10_codes: string[] | null;
          payload_opcs4_codes: string[] | null;
          row_kind: Database["public"]["Enums"]["outbound_write_back_row_kind"];
          s3_bucket: string | null;
          s3_key: string | null;
          sent_back_at: string | null;
          skip_origin: Database["public"]["Enums"]["write_back_skip_origin"] | null;
          skipped_at: string | null;
          status: Database["public"]["Enums"]["outbound_s3_write_status"];
          supersedes: string | null;
          triggering_user_id: string | null;
          trust_supplied: boolean;
          unchanged_for_icr_id: string | null;
          written_to_s3_at: string | null;
        };
        Insert: {
          auto_writeback_rule_id?: string | null;
          built_from_icd10_codes?: string[] | null;
          built_from_opcs4_codes?: string[] | null;
          created_at?: string;
          customer_id: string;
          encounter_id: string;
          error_message?: string | null;
          human_review_id?: string | null;
          id?: string;
          inbound_classification_result_id?: string | null;
          payload_icd10_codes?: string[] | null;
          payload_opcs4_codes?: string[] | null;
          row_kind?: Database["public"]["Enums"]["outbound_write_back_row_kind"];
          s3_bucket?: string | null;
          s3_key?: string | null;
          sent_back_at?: string | null;
          skip_origin?: Database["public"]["Enums"]["write_back_skip_origin"] | null;
          skipped_at?: string | null;
          status?: Database["public"]["Enums"]["outbound_s3_write_status"];
          supersedes?: string | null;
          triggering_user_id?: string | null;
          trust_supplied?: boolean;
          unchanged_for_icr_id?: string | null;
          written_to_s3_at?: string | null;
        };
        Update: {
          auto_writeback_rule_id?: string | null;
          built_from_icd10_codes?: string[] | null;
          built_from_opcs4_codes?: string[] | null;
          created_at?: string;
          customer_id?: string;
          encounter_id?: string;
          error_message?: string | null;
          human_review_id?: string | null;
          id?: string;
          inbound_classification_result_id?: string | null;
          payload_icd10_codes?: string[] | null;
          payload_opcs4_codes?: string[] | null;
          row_kind?: Database["public"]["Enums"]["outbound_write_back_row_kind"];
          s3_bucket?: string | null;
          s3_key?: string | null;
          sent_back_at?: string | null;
          skip_origin?: Database["public"]["Enums"]["write_back_skip_origin"] | null;
          skipped_at?: string | null;
          status?: Database["public"]["Enums"]["outbound_s3_write_status"];
          supersedes?: string | null;
          triggering_user_id?: string | null;
          trust_supplied?: boolean;
          unchanged_for_icr_id?: string | null;
          written_to_s3_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "outbound_result_write_backs_auto_writeback_rule_id_fkey";
            columns: ["auto_writeback_rule_id"];
            isOneToOne: false;
            referencedRelation: "automated_writeback_rules";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_auto_writeback_rule_id_fkey";
            columns: ["auto_writeback_rule_id"];
            isOneToOne: false;
            referencedRelation: "v_writeback_rules_for_analytics";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_human_review_id_fkey";
            columns: ["human_review_id"];
            isOneToOne: false;
            referencedRelation: "encounter_human_review";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_human_review_id_fkey";
            columns: ["human_review_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["review_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_supersedes_fkey";
            columns: ["supersedes", "encounter_id"];
            isOneToOne: false;
            referencedRelation: "outbound_result_write_backs";
            referencedColumns: ["id", "encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_supersedes_fkey";
            columns: ["supersedes", "encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_write_backs_for_analytics";
            referencedColumns: ["id", "encounter_id"];
          },
        ];
      };
      patient_documents: {
        Row: {
          content_hash: string | null;
          created_at: string | null;
          customer_id: string;
          document_type: string | null;
          file_date: string;
          file_identifier: Database["public"]["Enums"]["file_identifier"];
          id: string;
          patient_age: number;
          patient_id: string;
          patient_sex: Database["public"]["Enums"]["patient_sexes"];
          processed_at: string;
          s3_bucket: string;
          s3_key: string;
          source_fetched_at: string | null;
          source_revision: string | null;
          updated_at: string;
          withdrawn_at: string | null;
        };
        Insert: {
          content_hash?: string | null;
          created_at?: string | null;
          customer_id: string;
          document_type?: string | null;
          file_date: string;
          file_identifier: Database["public"]["Enums"]["file_identifier"];
          id?: string;
          patient_age: number;
          patient_id: string;
          patient_sex?: Database["public"]["Enums"]["patient_sexes"];
          processed_at: string;
          s3_bucket: string;
          s3_key: string;
          source_fetched_at?: string | null;
          source_revision?: string | null;
          updated_at?: string;
          withdrawn_at?: string | null;
        };
        Update: {
          content_hash?: string | null;
          created_at?: string | null;
          customer_id?: string;
          document_type?: string | null;
          file_date?: string;
          file_identifier?: Database["public"]["Enums"]["file_identifier"];
          id?: string;
          patient_age?: number;
          patient_id?: string;
          patient_sex?: Database["public"]["Enums"]["patient_sexes"];
          processed_at?: string;
          s3_bucket?: string;
          s3_key?: string;
          source_fetched_at?: string | null;
          source_revision?: string | null;
          updated_at?: string;
          withdrawn_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "patient_documents_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_documents_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      patient_encounter_medical_text_examples: {
        Row: {
          created_at: string;
          id: string;
          medical_text_example_id: string;
          patient_encounter_id: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          medical_text_example_id: string;
          patient_encounter_id: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          medical_text_example_id?: string;
          patient_encounter_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "patient_encounter_medical_text_exa_medical_text_example_id_fkey";
            columns: ["medical_text_example_id"];
            isOneToOne: true;
            referencedRelation: "medical_text_examples";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "patient_encounter_medical_text_exampl_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      patient_encounters: {
        Row: {
          admit_datetime: string | null;
          created_at: string | null;
          csn: string | null;
          customer_id: string;
          discharge_datetime: string | null;
          id: string;
          patient_age: number;
          patient_id: string;
          patient_sex: Database["public"]["Enums"]["patient_sexes"];
          specialty: Database["public"]["Enums"]["encounter_speciality_type"];
        };
        Insert: {
          admit_datetime?: string | null;
          created_at?: string | null;
          csn?: string | null;
          customer_id: string;
          discharge_datetime?: string | null;
          id?: string;
          patient_age: number;
          patient_id: string;
          patient_sex?: Database["public"]["Enums"]["patient_sexes"];
          specialty: Database["public"]["Enums"]["encounter_speciality_type"];
        };
        Update: {
          admit_datetime?: string | null;
          created_at?: string | null;
          csn?: string | null;
          customer_id?: string;
          discharge_datetime?: string | null;
          id?: string;
          patient_age?: number;
          patient_id?: string;
          patient_sex?: Database["public"]["Enums"]["patient_sexes"];
          specialty?: Database["public"]["Enums"]["encounter_speciality_type"];
        };
        Relationships: [
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      patient_enrichment: {
        Row: {
          created_at: string | null;
          date_of_birth: string;
          first_name: string;
          gui_id: string;
          id: string;
          last_name: string;
          mrn: string;
          nhs_number: string | null;
          provider_id: string;
          sex: string | null;
          updated_at: string | null;
        };
        Insert: {
          created_at?: string | null;
          date_of_birth: string;
          first_name: string;
          gui_id: string;
          id?: string;
          last_name: string;
          mrn: string;
          nhs_number?: string | null;
          provider_id: string;
          sex?: string | null;
          updated_at?: string | null;
        };
        Update: {
          created_at?: string | null;
          date_of_birth?: string;
          first_name?: string;
          gui_id?: string;
          id?: string;
          last_name?: string;
          mrn?: string;
          nhs_number?: string | null;
          provider_id?: string;
          sex?: string | null;
          updated_at?: string | null;
        };
        Relationships: [];
      };
      patient_medical_text_examples: {
        Row: {
          created_at: string;
          id: string;
          medical_text_example_id: string;
          patient_id: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          medical_text_example_id: string;
          patient_id: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          medical_text_example_id?: string;
          patient_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "patient_medical_text_examples_medical_text_example_id_fkey";
            columns: ["medical_text_example_id"];
            isOneToOne: true;
            referencedRelation: "medical_text_examples";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_medical_text_examples_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      patient_observations: {
        Row: {
          clinician_firstname: string | null;
          clinician_id: string | null;
          clinician_lastname: string | null;
          clinician_prefix: string | null;
          clinician_type: string | null;
          created_at: string | null;
          finnbr: string | null;
          id: string;
          observation_date: string;
          observation_gui_id: string;
          observation_identifier: string;
          patient_id: string;
          source_facility: string | null;
          source_ward: string | null;
          updated_at: string | null;
          visit_number: string | null;
        };
        Insert: {
          clinician_firstname?: string | null;
          clinician_id?: string | null;
          clinician_lastname?: string | null;
          clinician_prefix?: string | null;
          clinician_type?: string | null;
          created_at?: string | null;
          finnbr?: string | null;
          id?: string;
          observation_date: string;
          observation_gui_id: string;
          observation_identifier: string;
          patient_id: string;
          source_facility?: string | null;
          source_ward?: string | null;
          updated_at?: string | null;
          visit_number?: string | null;
        };
        Update: {
          clinician_firstname?: string | null;
          clinician_id?: string | null;
          clinician_lastname?: string | null;
          clinician_prefix?: string | null;
          clinician_type?: string | null;
          created_at?: string | null;
          finnbr?: string | null;
          id?: string;
          observation_date?: string;
          observation_gui_id?: string;
          observation_identifier?: string;
          patient_id?: string;
          source_facility?: string | null;
          source_ward?: string | null;
          updated_at?: string | null;
          visit_number?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "patient_observations_patient_id_patient_enrichment_gui_id_fk";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patient_enrichment";
            referencedColumns: ["gui_id"];
          },
        ];
      };
      patients: {
        Row: {
          created_at: string | null;
          customer_id: string;
          id: string;
        };
        Insert: {
          created_at?: string | null;
          customer_id: string;
          id?: string;
        };
        Update: {
          created_at?: string | null;
          customer_id?: string;
          id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "patients_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
        ];
      };
      permissions: {
        Row: {
          action: string;
          id: string;
          resource: string;
        };
        Insert: {
          action: string;
          id?: string;
          resource: string;
        };
        Update: {
          action?: string;
          id?: string;
          resource?: string;
        };
        Relationships: [];
      };
      profiles: {
        Row: {
          created_at: string;
          display_color: string;
          display_name: string;
          id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          display_color?: string;
          display_name: string;
          id: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          display_color?: string;
          display_name?: string;
          id?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      raw_bundle_identifications: {
        Row: {
          confidence: Database["public"]["Enums"]["identification_confidence"];
          identified_at: string;
          identified_type: Database["public"]["Enums"]["identified_document_type"];
          model: string;
          prompt_version: number;
          raw_bundle_id: string;
        };
        Insert: {
          confidence: Database["public"]["Enums"]["identification_confidence"];
          identified_at?: string;
          identified_type: Database["public"]["Enums"]["identified_document_type"];
          model: string;
          prompt_version: number;
          raw_bundle_id: string;
        };
        Update: {
          confidence?: Database["public"]["Enums"]["identification_confidence"];
          identified_at?: string;
          identified_type?: Database["public"]["Enums"]["identified_document_type"];
          model?: string;
          prompt_version?: number;
          raw_bundle_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "raw_bundle_identifications_raw_bundle_id_fkey";
            columns: ["raw_bundle_id"];
            isOneToOne: true;
            referencedRelation: "raw_files_bundles";
            referencedColumns: ["id"];
          },
        ];
      };
      raw_files_bundles: {
        Row: {
          bucket: string;
          bundle_date: string | null;
          bundle_type: Database["public"]["Enums"]["file_identifier"] | null;
          created_at: string;
          files: Json;
          id: string;
          metadata: Json | null;
          patient_id: string;
          source: Database["public"]["Enums"]["raw_files_bundle_source"];
        };
        Insert: {
          bucket: string;
          bundle_date?: string | null;
          bundle_type?: Database["public"]["Enums"]["file_identifier"] | null;
          created_at?: string;
          files: Json;
          id: string;
          metadata?: Json | null;
          patient_id: string;
          source: Database["public"]["Enums"]["raw_files_bundle_source"];
        };
        Update: {
          bucket?: string;
          bundle_date?: string | null;
          bundle_type?: Database["public"]["Enums"]["file_identifier"] | null;
          created_at?: string;
          files?: Json;
          id?: string;
          metadata?: Json | null;
          patient_id?: string;
          source?: Database["public"]["Enums"]["raw_files_bundle_source"];
        };
        Relationships: [
          {
            foreignKeyName: "raw_files_bundles_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      rekey_map_rft_legacy: {
        Row: {
          mrn: string;
          new_id: string;
          old_id: string;
          provider_id: string;
        };
        Insert: {
          mrn: string;
          new_id: string;
          old_id: string;
          provider_id: string;
        };
        Update: {
          mrn?: string;
          new_id?: string;
          old_id?: string;
          provider_id?: string;
        };
        Relationships: [];
      };
      review_queue_settings: {
        Row: {
          id: string;
          provider_priority_order: Database["public"]["Enums"]["customer_provider_id"][];
          updated_at: string;
          updated_by: string | null;
        };
        Insert: {
          id?: string;
          provider_priority_order?: Database["public"]["Enums"]["customer_provider_id"][];
          updated_at?: string;
          updated_by?: string | null;
        };
        Update: {
          id?: string;
          provider_priority_order?: Database["public"]["Enums"]["customer_provider_id"][];
          updated_at?: string;
          updated_by?: string | null;
        };
        Relationships: [];
      };
      review_verdicts: {
        Row: {
          admin_id: string;
          concern_index: number;
          created_at: string;
          customer_id: string;
          reason: string | null;
          review_id: string;
          reviewer_id: string;
          run_id: string;
          updated_at: string;
          verdict: Database["public"]["Enums"]["review_verdict_kind"];
        };
        Insert: {
          admin_id: string;
          concern_index: number;
          created_at?: string;
          customer_id: string;
          reason?: string | null;
          review_id: string;
          reviewer_id: string;
          run_id: string;
          updated_at?: string;
          verdict: Database["public"]["Enums"]["review_verdict_kind"];
        };
        Update: {
          admin_id?: string;
          concern_index?: number;
          created_at?: string;
          customer_id?: string;
          reason?: string | null;
          review_id?: string;
          reviewer_id?: string;
          run_id?: string;
          updated_at?: string;
          verdict?: Database["public"]["Enums"]["review_verdict_kind"];
        };
        Relationships: [];
      };
      role_permissions: {
        Row: {
          id: string;
          permission_id: string;
          role: Database["public"]["Enums"]["app_role"];
        };
        Insert: {
          id?: string;
          permission_id: string;
          role: Database["public"]["Enums"]["app_role"];
        };
        Update: {
          id?: string;
          permission_id?: string;
          role?: Database["public"]["Enums"]["app_role"];
        };
        Relationships: [
          {
            foreignKeyName: "role_permissions_permission_id_fkey";
            columns: ["permission_id"];
            isOneToOne: false;
            referencedRelation: "permissions";
            referencedColumns: ["id"];
          },
        ];
      };
      rpa_scrape_triggers: {
        Row: {
          customer_id: string;
          encounter_id: string;
          id: string;
          patient_id: string;
          specialty: string;
          status: string;
          triggered_at: string;
        };
        Insert: {
          customer_id: string;
          encounter_id: string;
          id?: string;
          patient_id: string;
          specialty: string;
          status?: string;
          triggered_at?: string;
        };
        Update: {
          customer_id?: string;
          encounter_id?: string;
          id?: string;
          patient_id?: string;
          specialty?: string;
          status?: string;
          triggered_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "rpa_scrape_triggers_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      run_output_co_morbidities: {
        Row: {
          classification_run_results_id: string | null;
          code_id: string;
          id: string;
        };
        Insert: {
          classification_run_results_id?: string | null;
          code_id: string;
          id: string;
        };
        Update: {
          classification_run_results_id?: string | null;
          code_id?: string;
          id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "run_output_co_morbidities_classification_run_results_id_fkey";
            columns: ["classification_run_results_id"];
            isOneToOne: false;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "run_output_co_morbidities_code_id_fkey";
            columns: ["code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
        ];
      };
      run_output_diagnosis: {
        Row: {
          classification_run_results_id: string;
          code_id: string;
          id: string;
        };
        Insert: {
          classification_run_results_id: string;
          code_id: string;
          id: string;
        };
        Update: {
          classification_run_results_id?: string;
          code_id?: string;
          id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "run_output_diagnosis_classification_run_results_id_fkey";
            columns: ["classification_run_results_id"];
            isOneToOne: false;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "run_output_diagnosis_code_id_fkey";
            columns: ["code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
        ];
      };
      run_output_procedures: {
        Row: {
          classification_run_results_id: string | null;
          code_id: string;
          id: string;
        };
        Insert: {
          classification_run_results_id?: string | null;
          code_id: string;
          id: string;
        };
        Update: {
          classification_run_results_id?: string | null;
          code_id?: string;
          id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "run_output_procedures_classification_run_results_id_fkey";
            columns: ["classification_run_results_id"];
            isOneToOne: false;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "run_output_procedures_code_id_fkey";
            columns: ["code_id"];
            isOneToOne: false;
            referencedRelation: "opcs4_codes";
            referencedColumns: ["id"];
          },
        ];
      };
      sact_document_data: {
        Row: {
          administration_date: string;
          administration_route: Json | null;
          chemo_radiation: string | null;
          clinical_trial: boolean | null;
          created_at: string | null;
          cycle_number: number;
          cycles_planned: number;
          date_of_death: string | null;
          date_of_decision_to_treat: string | null;
          document_id: string;
          drug_name: Json;
          intent_of_treatment: string | null;
          is_first_day_of_cycle: boolean;
          morphology: Json | null;
          opcs_delivery_code: Json | null;
          opcs_procurement_code: Json | null;
          performance_status: number;
          primary_diagnosis: Json | null;
          programme_number: string | null;
          regimen: Json;
          regimen_number: number;
          stage_of_disease: string | null;
          start_date_of_cycle: string;
          start_date_of_regimen: string | null;
          updated_at: string;
        };
        Insert: {
          administration_date: string;
          administration_route?: Json | null;
          chemo_radiation?: string | null;
          clinical_trial?: boolean | null;
          created_at?: string | null;
          cycle_number: number;
          cycles_planned: number;
          date_of_death?: string | null;
          date_of_decision_to_treat?: string | null;
          document_id: string;
          drug_name: Json;
          intent_of_treatment?: string | null;
          is_first_day_of_cycle: boolean;
          morphology?: Json | null;
          opcs_delivery_code?: Json | null;
          opcs_procurement_code?: Json | null;
          performance_status: number;
          primary_diagnosis?: Json | null;
          programme_number?: string | null;
          regimen: Json;
          regimen_number: number;
          stage_of_disease?: string | null;
          start_date_of_cycle: string;
          start_date_of_regimen?: string | null;
          updated_at?: string;
        };
        Update: {
          administration_date?: string;
          administration_route?: Json | null;
          chemo_radiation?: string | null;
          clinical_trial?: boolean | null;
          created_at?: string | null;
          cycle_number?: number;
          cycles_planned?: number;
          date_of_death?: string | null;
          date_of_decision_to_treat?: string | null;
          document_id?: string;
          drug_name?: Json;
          intent_of_treatment?: string | null;
          is_first_day_of_cycle?: boolean;
          morphology?: Json | null;
          opcs_delivery_code?: Json | null;
          opcs_procurement_code?: Json | null;
          performance_status?: number;
          primary_diagnosis?: Json | null;
          programme_number?: string | null;
          regimen?: Json;
          regimen_number?: number;
          stage_of_disease?: string | null;
          start_date_of_cycle?: string;
          start_date_of_regimen?: string | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "sact_document_data_document_id_fkey";
            columns: ["document_id"];
            isOneToOne: true;
            referencedRelation: "patient_documents";
            referencedColumns: ["id"];
          },
        ];
      };
      smart_tags: {
        Row: {
          created_at: string;
          datapoint_id: string;
          id: string;
          tag_value: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          datapoint_id: string;
          id?: string;
          tag_value: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          datapoint_id?: string;
          id?: string;
          tag_value?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "smart_tags_datapoint_id_fkey";
            columns: ["datapoint_id"];
            isOneToOne: false;
            referencedRelation: "datapoints";
            referencedColumns: ["id"];
          },
        ];
      };
      template_field_branching: {
        Row: {
          condition_field_id: string;
          condition_value: string;
          created_at: string;
          datapoint_id: string | null;
          id: string;
          template_field_id: string;
          updated_at: string;
        };
        Insert: {
          condition_field_id: string;
          condition_value: string;
          created_at?: string;
          datapoint_id?: string | null;
          id?: string;
          template_field_id: string;
          updated_at?: string;
        };
        Update: {
          condition_field_id?: string;
          condition_value?: string;
          created_at?: string;
          datapoint_id?: string | null;
          id?: string;
          template_field_id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "template_field_branching_condition_field_id_fkey";
            columns: ["condition_field_id"];
            isOneToOne: false;
            referencedRelation: "fields";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "template_field_branching_datapoint_id_fkey";
            columns: ["datapoint_id"];
            isOneToOne: false;
            referencedRelation: "datapoints";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "template_field_branching_template_field_id_fkey";
            columns: ["template_field_id"];
            isOneToOne: false;
            referencedRelation: "template_fields";
            referencedColumns: ["id"];
          },
        ];
      };
      template_field_filters: {
        Row: {
          created_at: string;
          filter_id: string;
          id: string;
          is_secondary: boolean | null;
          template_field_id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          filter_id: string;
          id?: string;
          is_secondary?: boolean | null;
          template_field_id: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          filter_id?: string;
          id?: string;
          is_secondary?: boolean | null;
          template_field_id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "template_field_filters_filter_id_fkey";
            columns: ["filter_id"];
            isOneToOne: false;
            referencedRelation: "filters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "template_field_filters_template_field_id_fkey";
            columns: ["template_field_id"];
            isOneToOne: false;
            referencedRelation: "template_fields";
            referencedColumns: ["id"];
          },
        ];
      };
      template_field_rule_sets: {
        Row: {
          created_at: string;
          description: string | null;
          id: string;
          name: string;
          order_index: number;
          template_field_id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          description?: string | null;
          id?: string;
          name: string;
          order_index?: number;
          template_field_id: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          description?: string | null;
          id?: string;
          name?: string;
          order_index?: number;
          template_field_id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "template_field_rule_sets_template_field_id_fkey";
            columns: ["template_field_id"];
            isOneToOne: false;
            referencedRelation: "template_fields";
            referencedColumns: ["id"];
          },
        ];
      };
      template_field_rules: {
        Row: {
          created_at: string;
          datapoint_id: string | null;
          field_id: string;
          id: string;
          operator: Database["public"]["Enums"]["rule_operator"];
          order_index: number;
          rule_set_id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          datapoint_id?: string | null;
          field_id: string;
          id?: string;
          operator: Database["public"]["Enums"]["rule_operator"];
          order_index?: number;
          rule_set_id: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          datapoint_id?: string | null;
          field_id?: string;
          id?: string;
          operator?: Database["public"]["Enums"]["rule_operator"];
          order_index?: number;
          rule_set_id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "template_field_rules_datapoint_id_fkey";
            columns: ["datapoint_id"];
            isOneToOne: false;
            referencedRelation: "datapoints";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "template_field_rules_field_id_fkey";
            columns: ["field_id"];
            isOneToOne: false;
            referencedRelation: "fields";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "template_field_rules_rule_set_id_fkey";
            columns: ["rule_set_id"];
            isOneToOne: false;
            referencedRelation: "template_field_rule_sets";
            referencedColumns: ["id"];
          },
        ];
      };
      template_fields: {
        Row: {
          created_at: string;
          field_id: string;
          id: string;
          is_visible: boolean;
          order_index: number;
          template_id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          field_id: string;
          id?: string;
          is_visible?: boolean;
          order_index: number;
          template_id: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          field_id?: string;
          id?: string;
          is_visible?: boolean;
          order_index?: number;
          template_id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "template_fields_field_id_fkey";
            columns: ["field_id"];
            isOneToOne: false;
            referencedRelation: "fields";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "template_fields_template_id_fkey";
            columns: ["template_id"];
            isOneToOne: false;
            referencedRelation: "templates";
            referencedColumns: ["id"];
          },
        ];
      };
      template_search_terms: {
        Row: {
          created_at: string;
          id: string;
          template_id: string;
          term: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          template_id: string;
          term: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          template_id?: string;
          term?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "template_search_terms_template_id_fkey";
            columns: ["template_id"];
            isOneToOne: false;
            referencedRelation: "templates";
            referencedColumns: ["id"];
          },
        ];
      };
      templates: {
        Row: {
          created_at: string;
          id: string;
          name: string;
          thumbnail_url: string | null;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          name: string;
          thumbnail_url?: string | null;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          name?: string;
          thumbnail_url?: string | null;
          updated_at?: string;
        };
        Relationships: [];
      };
      test_classification_run_results: {
        Row: {
          error_message: string | null;
          id: string;
          llm_model_id: string | null;
          run_end_time: string | null;
          run_start_time: string | null;
          success: boolean | null;
        };
        Insert: {
          error_message?: string | null;
          id?: string;
          llm_model_id?: string | null;
          run_end_time?: string | null;
          run_start_time?: string | null;
          success?: boolean | null;
        };
        Update: {
          error_message?: string | null;
          id?: string;
          llm_model_id?: string | null;
          run_end_time?: string | null;
          run_start_time?: string | null;
          success?: boolean | null;
        };
        Relationships: [];
      };
      test_combine_endo_histo_results: {
        Row: {
          id: string;
          result: Json | null;
          test_classification_run_results_id: string;
        };
        Insert: {
          id?: string;
          result?: Json | null;
          test_classification_run_results_id: string;
        };
        Update: {
          id?: string;
          result?: Json | null;
          test_classification_run_results_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "test_combine_endo_histo_results_run_results_id_unique";
            columns: ["test_classification_run_results_id"];
            isOneToOne: true;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
        ];
      };
      test_consolidate_diagnoses_results: {
        Row: {
          id: string;
          result: Json | null;
          test_classification_run_results_id: string;
        };
        Insert: {
          id?: string;
          result?: Json | null;
          test_classification_run_results_id: string;
        };
        Update: {
          id?: string;
          result?: Json | null;
          test_classification_run_results_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "test_consolidate_diagnoses_results_test_run_results_id_unique";
            columns: ["test_classification_run_results_id"];
            isOneToOne: true;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
        ];
      };
      test_data_run_regressions: {
        Row: {
          created_at: string;
          failure_classification_run_result_id: string;
          id: string;
          success_classification_run_result_id: string;
          viewed: boolean;
        };
        Insert: {
          created_at?: string;
          failure_classification_run_result_id: string;
          id?: string;
          success_classification_run_result_id: string;
          viewed?: boolean;
        };
        Update: {
          created_at?: string;
          failure_classification_run_result_id?: string;
          id?: string;
          success_classification_run_result_id?: string;
          viewed?: boolean;
        };
        Relationships: [
          {
            foreignKeyName: "test_data_run_regressions_failure_classification_run_resul_fkey";
            columns: ["failure_classification_run_result_id"];
            isOneToOne: false;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "test_data_run_regressions_success_classification_run_resul_fkey";
            columns: ["success_classification_run_result_id"];
            isOneToOne: false;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
        ];
      };
      test_encounter_expected_diagnoses: {
        Row: {
          icd10_code_id: string;
          id: string;
          is_optional: boolean;
          order_index: number;
          patient_encounter_id: string;
        };
        Insert: {
          icd10_code_id: string;
          id?: string;
          is_optional?: boolean;
          order_index: number;
          patient_encounter_id: string;
        };
        Update: {
          icd10_code_id?: string;
          id?: string;
          is_optional?: boolean;
          order_index?: number;
          patient_encounter_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "test_encounter_expected_diagnoses_icd10_code_id_fkey";
            columns: ["icd10_code_id"];
            isOneToOne: false;
            referencedRelation: "icd10_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_diagnoses_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      test_encounter_expected_procedures: {
        Row: {
          id: string;
          opcs4_code_id: string;
          order_index: number;
          patient_encounter_id: string;
        };
        Insert: {
          id?: string;
          opcs4_code_id: string;
          order_index: number;
          patient_encounter_id: string;
        };
        Update: {
          id?: string;
          opcs4_code_id?: string;
          order_index?: number;
          patient_encounter_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "test_encounter_expected_procedures_opcs4_code_id_fkey";
            columns: ["opcs4_code_id"];
            isOneToOne: false;
            referencedRelation: "opcs4_codes";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_expected_procedures_patient_encounter_id";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      test_encounter_results: {
        Row: {
          id: string;
          patient_encounter_id: string;
          review_summary: string | null;
          should_flag_for_review: boolean;
          test_classification_run_results_id: string;
        };
        Insert: {
          id?: string;
          patient_encounter_id: string;
          review_summary?: string | null;
          should_flag_for_review?: boolean;
          test_classification_run_results_id: string;
        };
        Update: {
          id?: string;
          patient_encounter_id?: string;
          review_summary?: string | null;
          should_flag_for_review?: boolean;
          test_classification_run_results_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_results_patient_encounter_id_fkey";
            columns: ["patient_encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "test_encounter_results_test_run_results_id_unique";
            columns: ["test_classification_run_results_id"];
            isOneToOne: true;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
        ];
      };
      test_endoscopy_results: {
        Row: {
          id: string;
          result: Json | null;
          test_classification_run_results_id: string;
          test_data_id: string;
          workflow_run_id: string | null;
        };
        Insert: {
          id?: string;
          result?: Json | null;
          test_classification_run_results_id: string;
          test_data_id: string;
          workflow_run_id?: string | null;
        };
        Update: {
          id?: string;
          result?: Json | null;
          test_classification_run_results_id?: string;
          test_data_id?: string;
          workflow_run_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "test_endo_results_test_run_results_id_unique";
            columns: ["test_classification_run_results_id"];
            isOneToOne: true;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "test_endoscopy_results_test_data_id_fkey";
            columns: ["test_data_id"];
            isOneToOne: false;
            referencedRelation: "medical_text_examples";
            referencedColumns: ["id"];
          },
        ];
      };
      test_gp_summary_results: {
        Row: {
          id: string;
          result: Json | null;
          test_classification_run_results_id: string;
          test_data_id: string;
          workflow_run_id: string | null;
        };
        Insert: {
          id?: string;
          result?: Json | null;
          test_classification_run_results_id: string;
          test_data_id: string;
          workflow_run_id?: string | null;
        };
        Update: {
          id?: string;
          result?: Json | null;
          test_classification_run_results_id?: string;
          test_data_id?: string;
          workflow_run_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "test_gp_summary_results_test_data_id_fkey";
            columns: ["test_data_id"];
            isOneToOne: false;
            referencedRelation: "medical_text_examples";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "test_gp_summary_results_test_run_results_id_unique";
            columns: ["test_classification_run_results_id"];
            isOneToOne: true;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
        ];
      };
      test_histology_results: {
        Row: {
          id: string;
          result: Json | null;
          test_classification_run_results_id: string;
          test_data_id: string;
          workflow_run_id: string | null;
        };
        Insert: {
          id?: string;
          result?: Json | null;
          test_classification_run_results_id: string;
          test_data_id: string;
          workflow_run_id?: string | null;
        };
        Update: {
          id?: string;
          result?: Json | null;
          test_classification_run_results_id?: string;
          test_data_id?: string;
          workflow_run_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "test_histology_results_test_data_id_fkey";
            columns: ["test_data_id"];
            isOneToOne: false;
            referencedRelation: "medical_text_examples";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "test_histology_results_test_run_results_id_unique";
            columns: ["test_classification_run_results_id"];
            isOneToOne: true;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
        ];
      };
      test_run_diagnoses: {
        Row: {
          diagnosis_code: string;
          id: string;
          order_index: number;
          test_classification_run_results_id: string;
        };
        Insert: {
          diagnosis_code: string;
          id?: string;
          order_index: number;
          test_classification_run_results_id: string;
        };
        Update: {
          diagnosis_code?: string;
          id?: string;
          order_index?: number;
          test_classification_run_results_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "test_run_diagnosis_results_test_run_results_id_unique";
            columns: ["test_classification_run_results_id"];
            isOneToOne: false;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
        ];
      };
      test_run_hrgs: {
        Row: {
          hrg_id: string;
          id: string;
          test_encounter_results_id: string;
        };
        Insert: {
          hrg_id: string;
          id?: string;
          test_encounter_results_id: string;
        };
        Update: {
          hrg_id?: string;
          id?: string;
          test_encounter_results_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "test_run_hrgs_hrg_id_fkey";
            columns: ["hrg_id"];
            isOneToOne: false;
            referencedRelation: "hrgs";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "test_run_hrgs_test_encounter_results_id_fkey";
            columns: ["test_encounter_results_id"];
            isOneToOne: false;
            referencedRelation: "test_encounter_results";
            referencedColumns: ["id"];
          },
        ];
      };
      test_run_procedures: {
        Row: {
          id: string;
          order_index: number;
          procedure_code: string;
          test_classification_run_results_id: string;
        };
        Insert: {
          id?: string;
          order_index: number;
          procedure_code: string;
          test_classification_run_results_id: string;
        };
        Update: {
          id?: string;
          order_index?: number;
          procedure_code?: string;
          test_classification_run_results_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "test_run_procedures_test_run_results_id_unique";
            columns: ["test_classification_run_results_id"];
            isOneToOne: false;
            referencedRelation: "test_classification_run_results";
            referencedColumns: ["id"];
          },
        ];
      };
      user_roles: {
        Row: {
          id: string;
          role: Database["public"]["Enums"]["app_role"];
          user_id: string;
        };
        Insert: {
          id?: string;
          role: Database["public"]["Enums"]["app_role"];
          user_id: string;
        };
        Update: {
          id?: string;
          role?: Database["public"]["Enums"]["app_role"];
          user_id?: string;
        };
        Relationships: [];
      };
      well_baby_classification_specific_results: {
        Row: {
          classification_result_id: string;
          created_at: string;
          id: string;
          updated_at: string;
          was_excluded: boolean;
        };
        Insert: {
          classification_result_id: string;
          created_at?: string;
          id?: string;
          updated_at?: string;
          was_excluded?: boolean;
        };
        Update: {
          classification_result_id?: string;
          created_at?: string;
          id?: string;
          updated_at?: string;
          was_excluded?: boolean;
        };
        Relationships: [
          {
            foreignKeyName: "well_baby_specific_classification_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "well_baby_specific_classification_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "well_baby_specific_classification_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "well_baby_specific_classification_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "well_baby_specific_classification_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "well_baby_specific_classification_result_id_fkey";
            columns: ["classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
        ];
      };
    };
    Views: {
      classification_code_discrepancies: {
        Row: {
          bucket: string | null;
          classification_result_id: string | null;
          classified_at: string | null;
          code: string | null;
          discharge_datetime: string | null;
          encounter_id: string | null;
          langfuse_trace_id: string | null;
          provider_id: Database["public"]["Enums"]["customer_provider_id"] | null;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"] | null;
        };
        Relationships: [];
      };
      classification_code_outcomes: {
        Row: {
          classification_result_id: string | null;
          classified_at: string | null;
          code: string | null;
          code_system: string | null;
          discharge_datetime: string | null;
          encounter_id: string | null;
          is_primary_ai: boolean | null;
          is_primary_human: boolean | null;
          langfuse_trace_id: string | null;
          outcome: string | null;
          provider_id: Database["public"]["Enums"]["customer_provider_id"] | null;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"] | null;
        };
        Relationships: [];
      };
      clinical_code_transformation_outputs_view: {
        Row: {
          code_id: string | null;
          code_type: Database["public"]["Enums"]["clinical_code_type"] | null;
          created_at: string | null;
          id: string | null;
          transformation_id: string | null;
          updated_at: string | null;
        };
        Insert: {
          code_id?: string | null;
          code_type?: Database["public"]["Enums"]["clinical_code_type"] | null;
          created_at?: string | null;
          id?: never;
          transformation_id?: string | null;
          updated_at?: string | null;
        };
        Update: {
          code_id?: string | null;
          code_type?: Database["public"]["Enums"]["clinical_code_type"] | null;
          created_at?: string | null;
          id?: never;
          transformation_id?: string | null;
          updated_at?: string | null;
        };
        Relationships: [];
      };
      clinical_codes: {
        Row: {
          code: string | null;
          created_at: string | null;
          description: string | null;
          id: string | null;
          last_updated_at: string | null;
          last_updated_description: string | null;
          type: Database["public"]["Enums"]["clinical_code_type"] | null;
          updated_at: string | null;
        };
        Relationships: [];
      };
      encounter_results_approved_unwritten: {
        Row: {
          admit_datetime: string | null;
          customer_id: string | null;
          customer_label: string | null;
          discharge_datetime: string | null;
          encounter_id: string | null;
          hrg_id: string | null;
          observation_datetime: string | null;
          patient_age: number | null;
          patient_id: string | null;
          patient_sex: Database["public"]["Enums"]["patient_sexes"] | null;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_human_review_hrg_id_fkey";
            columns: ["hrg_id"];
            isOneToOne: false;
            referencedRelation: "hrgs";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      encounter_results_outstanding_reviews: {
        Row: {
          admit_datetime: string | null;
          classification_result_id: string | null;
          customer_id: string | null;
          discharge_datetime: string | null;
          encounter_id: string | null;
          hrg_id: string | null;
          notes: string | null;
          observation_datetime: string | null;
          outcome: Database["public"]["Enums"]["human_review_outcome"] | null;
          patient_age: number | null;
          patient_id: string | null;
          patient_sex: Database["public"]["Enums"]["patient_sexes"] | null;
          review_created_at: string | null;
          review_id: string | null;
          review_updated_at: string | null;
          reviewer_id: string | null;
          status: Database["public"]["Enums"]["human_review_status"] | null;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_human_review_hrg_id_fkey";
            columns: ["hrg_id"];
            isOneToOne: false;
            referencedRelation: "hrgs";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      encounter_results_unreviewed: {
        Row: {
          admit_datetime: string | null;
          classification_result_id: string | null;
          created_at: string | null;
          customer_id: string | null;
          discharge_datetime: string | null;
          encounter_id: string | null;
          observation_datetime: string | null;
          patient_age: number | null;
          patient_id: string | null;
          patient_sex: Database["public"]["Enums"]["patient_sexes"] | null;
          was_specimen_or_histology_taken: boolean | null;
        };
        Relationships: [
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      encounter_results_without_completed_reviews: {
        Row: {
          admit_datetime: string | null;
          classification_result_id: string | null;
          classified_at: string | null;
          created_at: string | null;
          customer_id: string | null;
          discharge_datetime: string | null;
          encounter_id: string | null;
          observation_datetime: string | null;
          patient_age: number | null;
          patient_id: string | null;
          patient_sex: Database["public"]["Enums"]["patient_sexes"] | null;
          was_specimen_or_histology_taken: boolean | null;
        };
        Relationships: [
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      encounter_reviews_by_reviewer: {
        Row: {
          approved_reviews: number | null;
          classified_at: string | null;
          completed_reviews: number | null;
          customer_id: string | null;
          rejected_reviews: number | null;
          reviewer_id: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "inbound_classification_results_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
        ];
      };
      encounter_with_approved_result_unwritten: {
        Row: {
          admit_datetime: string | null;
          customer_id: string | null;
          discharge_datetime: string | null;
          encounter_id: string | null;
          observation_datetime: string | null;
          patient_age: number | null;
          patient_id: string | null;
          patient_sex: Database["public"]["Enums"]["patient_sexes"] | null;
        };
        Insert: {
          admit_datetime?: string | null;
          customer_id?: string | null;
          discharge_datetime?: string | null;
          encounter_id?: string | null;
          observation_datetime?: never;
          patient_age?: number | null;
          patient_id?: string | null;
          patient_sex?: Database["public"]["Enums"]["patient_sexes"] | null;
        };
        Update: {
          admit_datetime?: string | null;
          customer_id?: string | null;
          discharge_datetime?: string | null;
          encounter_id?: string | null;
          observation_datetime?: never;
          patient_age?: number | null;
          patient_id?: string | null;
          patient_sex?: Database["public"]["Enums"]["patient_sexes"] | null;
        };
        Relationships: [
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      encounters_with_successful_classification_run: {
        Row: {
          admit_datetime: string | null;
          customer_id: string | null;
          encounter_id: string | null;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"] | null;
        };
        Relationships: [
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
        ];
      };
      v_audit_events_for_analytics: {
        Row: {
          created_at: string | null;
          encounter_id: string | null;
          id: string | null;
          payload: Json | null;
          type: string | null;
          user_id: string | null;
        };
        Insert: {
          created_at?: string | null;
          encounter_id?: string | null;
          id?: string | null;
          payload?: Json | null;
          type?: string | null;
          user_id?: string | null;
        };
        Update: {
          created_at?: string | null;
          encounter_id?: string | null;
          id?: string | null;
          payload?: Json | null;
          type?: string | null;
          user_id?: string | null;
        };
        Relationships: [];
      };
      v_encounter_results_approved_unwritten: {
        Row: {
          admit_datetime: string | null;
          customer_id: string | null;
          customer_label: string | null;
          discharge_datetime: string | null;
          encounter_id: string | null;
          hrg_id: string | null;
          observation_datetime: string | null;
          patient_age: number | null;
          patient_id: string | null;
          patient_sex: Database["public"]["Enums"]["patient_sexes"] | null;
        };
        Relationships: [
          {
            foreignKeyName: "encounter_human_review_hrg_id_fkey";
            columns: ["hrg_id"];
            isOneToOne: false;
            referencedRelation: "hrgs";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      v_encounter_results_unreviewed: {
        Row: {
          admit_datetime: string | null;
          classification_result_id: string | null;
          created_at: string | null;
          customer_id: string | null;
          discharge_datetime: string | null;
          encounter_id: string | null;
          observation_datetime: string | null;
          patient_age: number | null;
          patient_id: string | null;
          patient_sex: Database["public"]["Enums"]["patient_sexes"] | null;
          was_specimen_or_histology_taken: boolean | null;
        };
        Relationships: [
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "patient_encounters_patient_id_fkey";
            columns: ["patient_id"];
            isOneToOne: false;
            referencedRelation: "patients";
            referencedColumns: ["id"];
          },
        ];
      };
      v_encounter_reviewable: {
        Row: {
          customer_id: string | null;
          discharge_date: string | null;
          encounter_id: string | null;
          provider_id: Database["public"]["Enums"]["customer_provider_id"] | null;
          specialty: string | null;
        };
        Relationships: [];
      };
      v_encounter_sent_back: {
        Row: {
          customer_id: string | null;
          discharge_date: string | null;
          encounter_id: string | null;
          is_recode: boolean | null;
          provider_id: Database["public"]["Enums"]["customer_provider_id"] | null;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"] | null;
          status: Database["public"]["Enums"]["outbound_s3_write_status"] | null;
        };
        Relationships: [
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
        ];
      };
      v_encounters_with_successful_classification: {
        Row: {
          admit_datetime: string | null;
          customer_id: string | null;
          encounter_id: string | null;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"] | null;
        };
        Relationships: [
          {
            foreignKeyName: "patient_encounters_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
        ];
      };
      v_endoscopy_passed_classifications_for_analytics: {
        Row: {
          classified_at: string | null;
          day: string | null;
          encounter_id: string | null;
          icr_id: string | null;
          provider_id: Database["public"]["Enums"]["customer_provider_id"] | null;
        };
        Relationships: [
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "inbound_classification_results_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
        ];
      };
      v_profiles_for_analytics: {
        Row: {
          display_name: string | null;
          id: string | null;
        };
        Insert: {
          display_name?: string | null;
          id?: string | null;
        };
        Update: {
          display_name?: string | null;
          id?: string | null;
        };
        Relationships: [];
      };
      v_write_backs_for_analytics: {
        Row: {
          auto_writeback_rule_id: string | null;
          customer_id: string | null;
          encounter_id: string | null;
          human_review_id: string | null;
          id: string | null;
          inbound_classification_result_id: string | null;
          sent_back_at: string | null;
          status: Database["public"]["Enums"]["outbound_s3_write_status"] | null;
          triggering_user_id: string | null;
          written_to_s3_at: string | null;
        };
        Insert: {
          auto_writeback_rule_id?: string | null;
          customer_id?: string | null;
          encounter_id?: string | null;
          human_review_id?: string | null;
          id?: string | null;
          inbound_classification_result_id?: string | null;
          sent_back_at?: string | null;
          status?: Database["public"]["Enums"]["outbound_s3_write_status"] | null;
          triggering_user_id?: string | null;
          written_to_s3_at?: string | null;
        };
        Update: {
          auto_writeback_rule_id?: string | null;
          customer_id?: string | null;
          encounter_id?: string | null;
          human_review_id?: string | null;
          id?: string | null;
          inbound_classification_result_id?: string | null;
          sent_back_at?: string | null;
          status?: Database["public"]["Enums"]["outbound_s3_write_status"] | null;
          triggering_user_id?: string | null;
          written_to_s3_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "outbound_result_write_backs_auto_writeback_rule_id_fkey";
            columns: ["auto_writeback_rule_id"];
            isOneToOne: false;
            referencedRelation: "automated_writeback_rules";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_auto_writeback_rule_id_fkey";
            columns: ["auto_writeback_rule_id"];
            isOneToOne: false;
            referencedRelation: "v_writeback_rules_for_analytics";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_customer_id_fkey";
            columns: ["customer_id"];
            isOneToOne: false;
            referencedRelation: "customers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounter_with_approved_result_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "encounters_with_successful_classification_run";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "patient_encounters";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_approved_unwritten";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_sent_back";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_encounter_id_fkey";
            columns: ["encounter_id"];
            isOneToOne: false;
            referencedRelation: "v_encounters_with_successful_classification";
            referencedColumns: ["encounter_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_human_review_id_fkey";
            columns: ["human_review_id"];
            isOneToOne: false;
            referencedRelation: "encounter_human_review";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_human_review_id_fkey";
            columns: ["human_review_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["review_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_outstanding_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "encounter_results_without_completed_reviews";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "inbound_classification_results";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_encounter_results_unreviewed";
            referencedColumns: ["classification_result_id"];
          },
          {
            foreignKeyName: "outbound_result_write_backs_inbound_classification_result_id_fk";
            columns: ["inbound_classification_result_id"];
            isOneToOne: false;
            referencedRelation: "v_endoscopy_passed_classifications_for_analytics";
            referencedColumns: ["icr_id"];
          },
        ];
      };
      v_writeback_rules_for_analytics: {
        Row: {
          conditions: string | null;
          created_at: string | null;
          id: string | null;
          is_active: boolean | null;
          label: string | null;
          provider_id: Database["public"]["Enums"]["customer_provider_id"] | null;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"] | null;
          updated_at: string | null;
        };
        Insert: {
          conditions?: never;
          created_at?: string | null;
          id?: string | null;
          is_active?: boolean | null;
          label?: string | null;
          provider_id?: Database["public"]["Enums"]["customer_provider_id"] | null;
          specialty?: Database["public"]["Enums"]["encounter_speciality_type"] | null;
          updated_at?: string | null;
        };
        Update: {
          conditions?: never;
          created_at?: string | null;
          id?: string | null;
          is_active?: boolean | null;
          label?: string | null;
          provider_id?: Database["public"]["Enums"]["customer_provider_id"] | null;
          specialty?: Database["public"]["Enums"]["encounter_speciality_type"] | null;
          updated_at?: string | null;
        };
        Relationships: [];
      };
    };
    Functions: {
      authorize: {
        Args: { requested_action: string; requested_resource: string };
        Returns: boolean;
      };
      branalysis: { Args: { query: string }; Returns: Json };
      custom_access_token_hook: { Args: { event: Json }; Returns: Json };
      fail_stuck_pending_classification_results: {
        Args: never;
        Returns: undefined;
      };
      get_classification_results_review_summary: {
        Args: {
          classified_from?: string;
          classified_to?: string;
          customer_ids?: string[];
          limit_count?: number;
          offset_count?: number;
          only_completed?: boolean;
        };
        Returns: {
          any_approved: boolean;
          any_complete: boolean;
          any_rejected: boolean;
          approved_reviews: number;
          classified_at: string;
          completed_reviews: number;
          customer_id: string;
          id: string;
          rejected_reviews: number;
        }[];
      };
      get_classification_review_totals: {
        Args: {
          classified_from?: string;
          classified_to?: string;
          customer_ids?: string[];
        };
        Returns: {
          approved_count: number;
          rejected_count: number;
          total_count: number;
        }[];
      };
      get_classification_reviews_by_reviewer: {
        Args: {
          classified_from?: string;
          classified_to?: string;
          customer_ids?: string[];
          limit_count?: number;
          offset_count?: number;
        };
        Returns: {
          approved_count: number;
          customer_id: string;
          rejected_count: number;
          reviewer_id: string;
          total_count: number;
        }[];
      };
      get_clinical_codes_by_datapoints: {
        Args: { p_datapoint_ids: string[]; p_field_ids: string[] };
        Returns: {
          code_type: Database["public"]["Enums"]["clinical_code_type"];
          code_value: string;
          match_reason: string;
          transformation_id: string;
        }[];
      };
      get_datapoints_for_template_field:
        | {
            Args: { p_template_field_id: string };
            Returns: {
              created_at: string;
              dataset_id: string;
              id: string;
              name: string;
              updated_at: string;
            }[];
          }
        | {
            Args: {
              p_field_key?: string;
              p_limit?: number;
              p_offset?: number;
              p_template_id: number;
            };
            Returns: {
              created_at: string;
              datapoint_id: number;
              field_key: string;
              template_field_id: number;
              template_id: number;
              value: string;
            }[];
          };
      get_encounter_review_totals: {
        Args: {
          customer_ids?: string[];
          observed_from?: string;
          observed_to?: string;
        };
        Returns: {
          approved_count: number;
          rejected_count: number;
          total_count: number;
        }[];
      };
      get_encounter_reviews_by_reviewer: {
        Args: {
          customer_ids?: string[];
          encounter_from?: string;
          encounter_to?: string;
          limit_count?: number;
          offset_count?: number;
        };
        Returns: {
          approved_count: number;
          customer_id: string;
          rejected_count: number;
          reviewer_id: string;
          total_count: number;
        }[];
      };
      get_encounters_sent_back_to_customer: {
        Args: {
          p_customer_ids?: string[];
          p_discharge_date_from?: string;
          p_discharge_date_to?: string;
        };
        Returns: {
          count: number;
          specialty: Database["public"]["Enums"]["encounter_speciality_type"];
          status: Database["public"]["Enums"]["outbound_s3_write_status"];
        }[];
      };
      get_filtered_datapoints: {
        Args: { p_field_id: string; p_filter_id: string };
        Returns: {
          id: string;
          name: string;
          smart_tags: Json;
        }[];
      };
      get_matching_template_fields_by_rules: {
        Args: { p_selected_options: Json[]; p_template_id: string };
        Returns: {
          field_id: string;
          field_name: string;
          field_type: string;
          is_visible: boolean;
          order_index: number;
          template_field_id: string;
        }[];
      };
      get_satisfied_template_fields: {
        Args: { inputdata: Json; templateid: string };
        Returns: {
          template_field_id: string;
        }[];
      };
      match_icd_10_codes_vectorised: {
        Args: {
          match_count?: number;
          minimum_match_threshold?: number;
          query_embedding: string;
        };
        Returns: {
          alt_code: string;
          code: string;
          created_at: string;
          description: string;
          description_embedding: string;
          gender_mask: string;
          id: string;
          max_age: number;
          min_age: number;
          modifier_4: string;
          modifier_5: string;
          qualifiers: string;
          similarity: number;
          tree_description: string;
          usage: string;
          usage_uk: number;
        }[];
      };
      match_icd_10_coding_standards_5th_edition: {
        Args: { match_count?: number; query_embedding: string };
        Returns: {
          chunk_index: number;
          created_at: string;
          embedding: string;
          heading: string;
          id: string;
          page: number;
          section: string;
          similarity: number;
          text: string;
        }[];
      };
      migrate_branching_to_rule_sets: { Args: never; Returns: undefined };
      migrate_template_field_branching_datapoints: {
        Args: never;
        Returns: undefined;
      };
      monitor_endo_classifiable: {
        Args: {
          p_customer_ids: string[];
          r1e: string;
          r1s: string;
          r2e: string;
          r2s: string;
        };
        Returns: Json;
      };
      prune_audit_events: { Args: { retention?: string }; Returns: number };
      replace_test_encounter_expected_diagnoses: {
        Args: {
          p_new_rows: Database["public"]["CompositeTypes"]["test_encounter_expected_diagnosis_input"][];
          p_patient_encounter_id: string;
        };
        Returns: undefined;
      };
      replace_test_encounter_expected_procedures: {
        Args: {
          p_new_rows: Database["public"]["CompositeTypes"]["test_encounter_expected_procedure_input"][];
          p_patient_encounter_id: string;
        };
        Returns: undefined;
      };
      save_icd_10_code_rule: {
        Args: {
          p_code_ids: string[];
          p_customer_ids: string[];
          p_expected_updated_at: string;
          p_is_active: boolean;
          p_name: string;
          p_rule_id: string;
          p_rule_text: string;
          p_specialties: Database["public"]["Enums"]["encounter_speciality_type"][];
        };
        Returns: string;
      };
      set_human_review_diagnoses: {
        Args: {
          p_inbound_classification_result_id: string;
          p_reviewer_id: string;
          p_rows: Database["public"]["CompositeTypes"]["classification_review_diagnosis_input"][];
        };
        Returns: string;
      };
      set_human_review_procedures: {
        Args: {
          p_inbound_classification_result_id: string;
          p_reviewer_id: string;
          p_rows: Database["public"]["CompositeTypes"]["classification_review_procedure_input"][];
        };
        Returns: string;
      };
      show_limit: { Args: never; Returns: number };
      show_trgm: { Args: { "": string }; Returns: string[] };
    };
    Enums: {
      ai_operation_type: "template_selection" | "field_completion" | "template_transformation";
      app_role: "admin" | "clinical_coder_admin" | "clinical_coder";
      automation_decline_reason:
        | "day_one_of_cycle"
        | "day_of_cycle_underivable"
        | "cycle_dates_contradictory"
        | "no_prior_episode"
        | "prior_specialty_mismatch"
        | "prior_not_written_back"
        | "prior_has_no_icd10_codes"
        | "prior_outside_window"
        | "prior_not_strictly_earlier"
        | "source_tie_unresolved"
        | "encounter_under_review"
        | "run_cap_reached"
        | "no_opcs4_code"
        | "source_code_unresolvable"
        | "no_policy"
        | "not_sampled"
        | "flagged_for_review"
        | "no_icd10_codes"
        | "encounter_not_coded"
        | "admin_rerun";
      automation_strategy: "prior_reuse" | "sampled";
      chemo_automation_decline_reason:
        | "day_one_of_cycle"
        | "day_of_cycle_underivable"
        | "cycle_dates_contradictory"
        | "no_prior_episode"
        | "prior_specialty_mismatch"
        | "prior_not_written_back"
        | "prior_has_no_icd10_codes"
        | "prior_outside_window"
        | "prior_not_strictly_earlier"
        | "source_tie_unresolved"
        | "encounter_under_review"
        | "run_cap_reached"
        | "no_opcs4_code"
        | "source_code_unresolvable";
      chemo_coding_route: "generated" | "shadow_eligible" | "automated" | "revoked";
      classification_result_outcome: "pending" | "success" | "error";
      classification_run_origin: "pipeline" | "admin_rerun";
      clinical_code_type: "ICD-10" | "OPCS-4";
      codeable_summary_state_status: "accumulating" | "emitted" | "stuck";
      coding_investigation_confidence: "low" | "medium" | "high";
      coding_investigation_discrepancy_kind: "overcoded" | "undercoded" | "duplicated" | "wrong";
      coding_investigation_trigger: "manual" | "auto";
      coding_route: "generated" | "shadow_eligible" | "automated" | "revoked";
      customer_provider_id: "TEST" | "ELHT" | "ASP" | "BHT" | "UHCW" | "UCLH" | "RFT" | "LNW" | "THH" | "UHNM" | "RFL";
      encounter_grouping_status: "grouped" | "rejected" | "absent";
      encounter_relevance_assessment_status: "pending" | "processing" | "relevant" | "not_relevant" | "error";
      encounter_source_fetch_state:
        | "expected"
        | "requested"
        | "succeeded"
        | "succeeded_empty"
        | "failed"
        | "not_applicable";
      encounter_speciality_type:
        | "endoscopy"
        | "haematology"
        | "chemo"
        | "well_baby"
        | "ophthalmology"
        | "infusion"
        | "rfl_automation";
      field_type: "single_select" | "multi_select" | "nested_multi_select";
      file_identifier:
        | "adhoc_letter"
        | "cancer_mdt"
        | "clinic_letter"
        | "clinic_document"
        | "codeable_summary"
        | "discharge_cancelled"
        | "discharge"
        | "discharge_letter"
        | "endoscopy_appointment"
        | "infusion_appointment"
        | "endoscopy_histology"
        | "endoscopy_procedure"
        | "epic_note"
        | "epic_history_note"
        | "historic_codes"
        | "medisight_mdm_t02"
        | "nursing_notes"
        | "oncology_clinic_letter"
        | "pre_op_endo"
        | "problem_list"
        | "procedure_report"
        | "sact"
        | "referral_letter";
      form_read_correction_reason: "incorrect" | "abstained" | "no_mark_found";
      histology_site: "upper" | "lower" | "both" | "unknown";
      histology_verdict: "compatible" | "contradicts" | "unknown";
      histology_verdict_decider: "site_rule";
      human_review_outcome: "approved" | "rejected";
      human_review_status: "pending" | "complete";
      icd_index_code_family_type: "prefix" | "range_with_fourth";
      icd_index_code_role: "single" | "dagger" | "asterisk";
      icd_index_edge_type: "HAS_SUBTERM" | "SEE" | "SEE_ALSO" | "SEE_TABLE";
      icd_index_rule_severity: "hard" | "soft";
      icd_index_rule_type: "EXCLUDE" | "MUST_INCLUDE";
      icd_index_section: "I" | "II" | "III";
      icd_index_tag:
        | "infectious"
        | "neoplastic"
        | "congenital"
        | "pregnancy"
        | "puerperium"
        | "fetus_newborn"
        | "traumatic"
        | "psychogenic"
        | "external_cause"
        | "key_term";
      icd10_gender_restriction: "female" | "male";
      identification_confidence: "high" | "medium" | "low";
      identified_document_type:
        | "clinic_letter"
        | "referral_letter"
        | "cancer_mdt"
        | "gp_patient_summary"
        | "advice_note"
        | "open_access_gastroscopy"
        | "other";
      indication_verdict: "coded" | "not-coded";
      issue_status: "active" | "resolved";
      mediviewer_bundle_outcome: "fetched" | "filtered" | "written" | "empty" | "error";
      outbound_coding_status: "IN_PROGRESS" | "NOT_STARTED" | "AWAITING_HISTOLOGY" | "COMPLETED";
      outbound_coding_status_delivery: "pending" | "sent" | "failed";
      outbound_s3_write_status: "pending" | "success" | "error" | "rejected" | "skipped";
      outbound_write_back_row_kind: "attempt" | "refusal";
      patient_sexes: "M" | "F" | "U";
      raw_files_bundle_source:
        | "mediviewer"
        | "rft-fhir"
        | "hl7"
        | "rft-rpa-scrape"
        | "uhnm-preop-endo"
        | "bht-comorbidities"
        | "uhcw-rpa-scrape"
        | "uhcw-sact"
        | "epic-fhir";
      review_verdict_kind: "agree" | "overrule";
      rule_operator: "AND" | "NOT";
      screening_programme: "bcsp_fit_positive";
      transformation_operator: "AND" | "ONLY" | "NOT";
      write_back_skip_origin: "robot" | "operator" | "legacy_unchanged_codes" | "superseded";
    };
    CompositeTypes: {
      classification_review_diagnosis_input: {
        code: string | null;
        description: string | null;
        order_index: number | null;
      };
      classification_review_procedure_input: {
        code: string | null;
        description: string | null;
        order_index: number | null;
      };
      test_encounter_expected_diagnosis_input: {
        icd10_code_id: string | null;
        order_index: number | null;
        is_optional: boolean | null;
      };
      test_encounter_expected_procedure_input: {
        opcs4_code_id: string | null;
        order_index: number | null;
      };
    };
  };
};

type DatabaseWithoutInternals = Omit<Database, "__InternalSupabase">;

type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, "public">];

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends {
      Row: infer R;
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    ? (DefaultSchema["Tables"] & DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R;
      }
      ? R
      : never
    : never;

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"] | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Insert: infer I;
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I;
      }
      ? I
      : never
    : never;

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"] | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Update: infer U;
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U;
      }
      ? U
      : never
    : never;

export type Enums<
  DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"] | { schema: keyof DatabaseWithoutInternals },
  EnumName extends DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
    : never = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"]
    ? DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
    : never;

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema["CompositeTypes"]
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends PublicCompositeTypeNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"]
    : never = never,
> = PublicCompositeTypeNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"]
    ? DefaultSchema["CompositeTypes"][PublicCompositeTypeNameOrOptions]
    : never;

export const Constants = {
  graphql_public: {
    Enums: {},
  },
  public: {
    Enums: {
      ai_operation_type: ["template_selection", "field_completion", "template_transformation"],
      app_role: ["admin", "clinical_coder_admin", "clinical_coder"],
      automation_decline_reason: [
        "day_one_of_cycle",
        "day_of_cycle_underivable",
        "cycle_dates_contradictory",
        "no_prior_episode",
        "prior_specialty_mismatch",
        "prior_not_written_back",
        "prior_has_no_icd10_codes",
        "prior_outside_window",
        "prior_not_strictly_earlier",
        "source_tie_unresolved",
        "encounter_under_review",
        "run_cap_reached",
        "no_opcs4_code",
        "source_code_unresolvable",
        "no_policy",
        "not_sampled",
        "flagged_for_review",
        "no_icd10_codes",
        "encounter_not_coded",
        "admin_rerun",
      ],
      automation_strategy: ["prior_reuse", "sampled"],
      chemo_automation_decline_reason: [
        "day_one_of_cycle",
        "day_of_cycle_underivable",
        "cycle_dates_contradictory",
        "no_prior_episode",
        "prior_specialty_mismatch",
        "prior_not_written_back",
        "prior_has_no_icd10_codes",
        "prior_outside_window",
        "prior_not_strictly_earlier",
        "source_tie_unresolved",
        "encounter_under_review",
        "run_cap_reached",
        "no_opcs4_code",
        "source_code_unresolvable",
      ],
      chemo_coding_route: ["generated", "shadow_eligible", "automated", "revoked"],
      classification_result_outcome: ["pending", "success", "error"],
      classification_run_origin: ["pipeline", "admin_rerun"],
      clinical_code_type: ["ICD-10", "OPCS-4"],
      codeable_summary_state_status: ["accumulating", "emitted", "stuck"],
      coding_investigation_confidence: ["low", "medium", "high"],
      coding_investigation_discrepancy_kind: ["overcoded", "undercoded", "duplicated", "wrong"],
      coding_investigation_trigger: ["manual", "auto"],
      coding_route: ["generated", "shadow_eligible", "automated", "revoked"],
      customer_provider_id: ["TEST", "ELHT", "ASP", "BHT", "UHCW", "UCLH", "RFT", "LNW", "THH", "UHNM", "RFL"],
      encounter_grouping_status: ["grouped", "rejected", "absent"],
      encounter_relevance_assessment_status: ["pending", "processing", "relevant", "not_relevant", "error"],
      encounter_source_fetch_state: [
        "expected",
        "requested",
        "succeeded",
        "succeeded_empty",
        "failed",
        "not_applicable",
      ],
      encounter_speciality_type: [
        "endoscopy",
        "haematology",
        "chemo",
        "well_baby",
        "ophthalmology",
        "infusion",
        "rfl_automation",
      ],
      field_type: ["single_select", "multi_select", "nested_multi_select"],
      file_identifier: [
        "adhoc_letter",
        "cancer_mdt",
        "clinic_letter",
        "clinic_document",
        "codeable_summary",
        "discharge_cancelled",
        "discharge",
        "discharge_letter",
        "endoscopy_appointment",
        "infusion_appointment",
        "endoscopy_histology",
        "endoscopy_procedure",
        "epic_note",
        "epic_history_note",
        "historic_codes",
        "medisight_mdm_t02",
        "nursing_notes",
        "oncology_clinic_letter",
        "pre_op_endo",
        "problem_list",
        "procedure_report",
        "sact",
        "referral_letter",
      ],
      form_read_correction_reason: ["incorrect", "abstained", "no_mark_found"],
      histology_site: ["upper", "lower", "both", "unknown"],
      histology_verdict: ["compatible", "contradicts", "unknown"],
      histology_verdict_decider: ["site_rule"],
      human_review_outcome: ["approved", "rejected"],
      human_review_status: ["pending", "complete"],
      icd_index_code_family_type: ["prefix", "range_with_fourth"],
      icd_index_code_role: ["single", "dagger", "asterisk"],
      icd_index_edge_type: ["HAS_SUBTERM", "SEE", "SEE_ALSO", "SEE_TABLE"],
      icd_index_rule_severity: ["hard", "soft"],
      icd_index_rule_type: ["EXCLUDE", "MUST_INCLUDE"],
      icd_index_section: ["I", "II", "III"],
      icd_index_tag: [
        "infectious",
        "neoplastic",
        "congenital",
        "pregnancy",
        "puerperium",
        "fetus_newborn",
        "traumatic",
        "psychogenic",
        "external_cause",
        "key_term",
      ],
      icd10_gender_restriction: ["female", "male"],
      identification_confidence: ["high", "medium", "low"],
      identified_document_type: [
        "clinic_letter",
        "referral_letter",
        "cancer_mdt",
        "gp_patient_summary",
        "advice_note",
        "open_access_gastroscopy",
        "other",
      ],
      indication_verdict: ["coded", "not-coded"],
      issue_status: ["active", "resolved"],
      mediviewer_bundle_outcome: ["fetched", "filtered", "written", "empty", "error"],
      outbound_coding_status: ["IN_PROGRESS", "NOT_STARTED", "AWAITING_HISTOLOGY", "COMPLETED"],
      outbound_coding_status_delivery: ["pending", "sent", "failed"],
      outbound_s3_write_status: ["pending", "success", "error", "rejected", "skipped"],
      outbound_write_back_row_kind: ["attempt", "refusal"],
      patient_sexes: ["M", "F", "U"],
      raw_files_bundle_source: [
        "mediviewer",
        "rft-fhir",
        "hl7",
        "rft-rpa-scrape",
        "uhnm-preop-endo",
        "bht-comorbidities",
        "uhcw-rpa-scrape",
        "uhcw-sact",
        "epic-fhir",
      ],
      review_verdict_kind: ["agree", "overrule"],
      rule_operator: ["AND", "NOT"],
      screening_programme: ["bcsp_fit_positive"],
      transformation_operator: ["AND", "ONLY", "NOT"],
      write_back_skip_origin: ["robot", "operator", "legacy_unchanged_codes", "superseded"],
    },
  },
} as const;
